import React from 'react';
import { EmailBlockData } from '../ModernEmailBuilder';
import { getPlatformData } from '../utils/socialIcons';
import QRCode from 'qrcode';
import { normalizeHexColorForQr } from '../utils/qrEmailEmbed';
import { editorialMobile } from '../utils/editorialResponsiveCss';
import { headerMobile, resolveHeaderAccentColor, resolveHeaderNavColor } from '../utils/headerResponsiveCss';
import { resolveMenuStyles, type MenuLink } from '../utils/menuBlockUtils';
import { isHeroCoreTemplate } from '../utils/heroContentUtils';
import { isEffectivelyZeroPadding } from '../utils/textBlockTypography';
import {
  getHeroOverlayButtonInlineStyles,
  resolveHeroOverlayButtonConfig,
} from '../utils/heroOverlayButton';
import { HeroContentCoreBlock } from './HeroContentCoreBlock';
import { FooterBlock } from './FooterBlock';
import { isSvgStickerContent, resolveVideoThumbnailUrl } from '../utils/mediaBlockUtils';
import { FeaturesGridBlock } from './FeaturesGridBlock';
import { renderTestimonialHtml } from '../utils/testimonialHtml';

type TextColorRange = {
  start: number;
  end: number;
  color: string;
};

const normalizeColorRanges = (
  ranges: TextColorRange[] | undefined,
  textLength: number
): TextColorRange[] => {
  if (!Array.isArray(ranges) || textLength <= 0) return [];
  const cleaned: TextColorRange[] = [];
  ranges.forEach((range) => {
    if (!range || typeof range.color !== 'string') return;
    const color = range.color.trim();
    if (!color) return;
    const rawStart = typeof range.start === 'number' ? range.start : 0;
    const rawEnd = typeof range.end === 'number' ? range.end : textLength;
    const start = Math.max(0, Math.min(rawStart, textLength));
    const end = Math.max(start, Math.min(rawEnd, textLength));
    if (end <= start) return;
    cleaned.push({ start, end, color });
  });
  cleaned.sort((a, b) => {
    if (a.start === b.start) {
      return a.end - b.end;
    }
    return a.start - b.start;
  });
  const merged: TextColorRange[] = [];
  cleaned.forEach((current) => {
    const last = merged[merged.length - 1];
    if (last && last.color === current.color && current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  });
  return merged;
};

const buildActionHref = (linkType?: string, linkUrl?: string) => {
  // Handle 'none' linkType - no link action
  if (linkType === 'none') return '';
  const trimmed = (linkUrl || '').trim();
  if (!trimmed) return '';
  if (linkType === 'mailto') {
    return trimmed.toLowerCase().startsWith('mailto:') ? trimmed : `mailto:${trimmed}`;
  }
  if (linkType === 'tel') {
    const normalized = trimmed.replace(/\s+/g, '');
    return normalized.toLowerCase().startsWith('tel:') ? normalized : `tel:${normalized}`;
  }
  return trimmed;
};

const formatFontFamilyForRender = (fontFamily?: string | null, fallback = 'Arial, sans-serif'): string => {
  if (!fontFamily) return fallback;
  if (fontFamily.includes("'") || fontFamily.includes('"') || fontFamily.includes(',')) {
    return fontFamily;
  }
  if (fontFamily.includes(' ')) {
    return `'${fontFamily}', sans-serif`;
  }
  return `${fontFamily}, sans-serif`;
};

interface BlockRendererProps {
  block: EmailBlockData;
  isEditable?: boolean;
  onTextUpdate?: (text: string) => void;
  isSelected?: boolean;
  isMobile?: boolean;
  isPalettePreview?: boolean;
  globalFontFamily?: string;
}

/**
 * Standardizes padding parsing across all block types.
 * Supports shorthands (1-4 values) and explicit overrides.
 */
export function computePadding(styles: any, defaultPadding: string = '20px'): string {
  // 1. Extract explicit side-specific padding if present
  let top = styles.paddingTop;
  let right = styles.paddingRight;
  let bottom = styles.paddingBottom;
  let left = styles.paddingLeft;

  // 2. If shorthand is present in styles, use it for any side not explicitly defined
  const shorthandRaw = typeof styles.padding === 'string' ? styles.padding.trim() : undefined;
  const shorthand =
    shorthandRaw && !isEffectivelyZeroPadding(shorthandRaw) ? shorthandRaw : undefined;
  if (shorthand) {
    const parts = shorthand.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      if (top === undefined) top = parts[0];
      if (right === undefined) right = parts[0];
      if (bottom === undefined) bottom = parts[0];
      if (left === undefined) left = parts[0];
    } else if (parts.length === 2) {
      if (top === undefined) top = parts[0];
      if (right === undefined) right = parts[1];
      if (bottom === undefined) bottom = parts[0];
      if (left === undefined) left = parts[1];
    } else if (parts.length === 3) {
      if (top === undefined) top = parts[0];
      if (right === undefined) right = parts[1];
      if (bottom === undefined) bottom = parts[2];
      if (left === undefined) left = parts[1];
    } else if (parts.length >= 4) {
      if (top === undefined) top = parts[0];
      if (right === undefined) right = parts[1];
      if (bottom === undefined) bottom = parts[2];
      if (left === undefined) left = parts[3];
    }
  }

  // 3. Resolve defaultPadding into components to handle shorthand fallbacks correctly
  const dParts = defaultPadding.trim().split(/\s+/).filter(Boolean);
  let dTop, dRight, dBottom, dLeft;

  if (dParts.length === 1) {
    dTop = dRight = dBottom = dLeft = dParts[0];
  } else if (dParts.length === 2) {
    dTop = dBottom = dParts[0];
    dRight = dLeft = dParts[1];
  } else if (dParts.length === 3) {
    dTop = dParts[0];
    dRight = dLeft = dParts[1];
    dBottom = dParts[2];
  } else {
    dTop = dParts[0];
    dRight = dParts[1];
    dBottom = dParts[2];
    dLeft = dParts[3];
  }

  // 4. Combine with fallbacks
  const finalTop = top ?? dTop;
  const finalRight = right ?? dRight;
  const finalBottom = bottom ?? dBottom;
  const finalLeft = left ?? dLeft;

  return `${finalTop} ${finalRight} ${finalBottom} ${finalLeft}`;
}

const getSectionLayoutPadding = (layout: string | undefined, isMobile: boolean = false): string => {
  // Dark layouts get a bit more top padding for a bolder hero, light/tour layouts are flatter
  if (isMobile) {
    if (layout === 'light' || layout === 'tour') {
      return '24px 20px 24px 20px';
    }
    return '32px 20px 32px 20px';
  }
  if (layout === 'light' || layout === 'tour') {
    return '40px 20px 32px 20px';
  }
  return '48px 20px 32px 20px';
};

const MenuBlock: React.FC<{ content: any; styles: any; globalFontFamily?: string; isMobile?: boolean }> = ({
  content,
  styles,
  globalFontFamily,
  isMobile = false,
}) => {
  const links: MenuLink[] = Array.isArray(content.links) ? content.links : [];
  const resolved = resolveMenuStyles(styles, globalFontFamily, isMobile);
  const { container, item, justifyContent } = resolved;

  return (
    <div
      className="menu-block-wrap"
      style={{
        padding: computePadding(styles, container.padding),
        textAlign: container.textAlign,
        backgroundColor: container.backgroundColor,
        fontFamily: container.fontFamily,
        fontSize: container.fontSize,
        fontWeight: container.fontWeight,
        color: item.color,
        borderRadius: container.borderRadius,
        border: container.border,
        boxShadow: container.boxShadow,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {links.length > 0 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: justifyContent,
            flexWrap: 'wrap',
            alignItems: 'center',
            alignContent: 'center',
            gap: 0,
            width: '100%',
          }}
        >
          {links.map((link, index) => (
            <a
              key={`${link.label || 'link'}-${index}`}
              href={link.url || '#'}
              className="menu-item-link"
              onClick={(e) => e.preventDefault()}
              style={{
                color: item.color,
                textDecoration: item.textDecoration,
                display: 'inline-block',
                padding: item.padding,
                borderRadius: item.borderRadius,
                border: item.border,
                backgroundColor: item.backgroundColor,
                boxShadow: item.boxShadow,
                textTransform: item.textTransform as React.CSSProperties['textTransform'],
                letterSpacing: item.letterSpacing,
                fontSize: item.fontSize,
                fontWeight: item.fontWeight,
                fontFamily: container.fontFamily,
                whiteSpace: item.whiteSpace as React.CSSProperties['whiteSpace'],
                lineHeight: item.lineHeight,
                verticalAlign: item.verticalAlign as React.CSSProperties['verticalAlign'],
                margin: item.margin,
              }}
            >
              {link.label || 'Link'}
            </a>
          ))}
        </div>
      ) : (
        <span style={{ color: '#64748B' }}>Add menu links</span>
      )}
    </div>
  );
};

const HeaderBlock: React.FC<{ content: any; styles: any; globalFontFamily?: string; isMobile?: boolean }> = ({
  content,
  styles,
  globalFontFamily,
  isMobile = false,
}) => {
  const {
    layout = 'modern-clean',
    logoUrl,
    logoMaxWidth = '120px',
    links = [],
    navFontWeight = '600',
    navTextColor,
    accentLinkColor,
    actionText,
    actionUrl,
    actionButtonBg = '#FFFFFF',
    actionButtonColor = '#1E293B',
  } = content;

  const m = headerMobile;
  const bg = styles.backgroundColor || '#FFFFFF';
  const navColor = resolveHeaderNavColor(bg, navTextColor, layout);
  const fontFamily = styles.fontFamily || globalFontFamily || 'inherit';
  const finalLogoUrl = logoUrl || '/images/Email Builder/DEFAULT LOGO.png';
  const isCentered = layout === 'bold-centered' || layout === 'dark-accent';
  const parsedLogo = parseInt(String(logoMaxWidth).replace('px', ''), 10);
  const scaledLogoMax = isMobile
    ? `${Math.round((parsedLogo || 120) * m.logoMaxWidthScale)}px`
    : logoMaxWidth;

  const containerStyle: React.CSSProperties = {
    padding: computePadding(
      styles,
      isCentered
        ? (isMobile ? m.centeredPadding : styles.padding || '28px 20px')
        : isMobile
          ? m.padding
          : styles.padding || '16px 24px'
    ),
    backgroundColor: bg,
    width: '100%',
    boxSizing: 'border-box',
    fontFamily,
  };

  const linkStyle = (index: number): React.CSSProperties => {
    const accent = resolveHeaderAccentColor(layout, index, accentLinkColor);
    return {
      color: accent || navColor,
      fontWeight: navFontWeight,
      textDecoration: 'none',
      fontSize: isMobile ? m.navFontSize : '14px',
      fontFamily,
      textTransform: layout === 'bold-centered' ? 'uppercase' : 'none',
      letterSpacing: layout === 'bold-centered' ? '1px' : 'normal',
      whiteSpace: 'nowrap',
    };
  };

  const logoNode = finalLogoUrl ? (
    <img
      src={finalLogoUrl}
      alt="Logo"
      style={{
        maxWidth: scaledLogoMax,
        height: 'auto',
        display: 'block',
        margin: isCentered || isMobile ? '0 auto' : 0,
      }}
    />
  ) : (
    <div
      style={{
        width: scaledLogoMax,
        height: '36px',
        backgroundColor: '#E2E8F0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        color: '#64748B',
        margin: isCentered || isMobile ? '0 auto' : 0,
      }}
    >
      Logo
    </div>
  );

  const navLinks = (
    <div
      style={{
        display: 'flex',
        gap: isMobile ? m.navGap : isCentered ? '28px' : '24px',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}
    >
      {links.map((link: any, index: number) => (
        <a key={index} href={link.url || '#'} onClick={(e) => e.preventDefault()} style={linkStyle(index)}>
          {link.label}
        </a>
      ))}
    </div>
  );

  const actionBtn =
    layout === 'action' && actionText ? (
      <a
        href={actionUrl || '#'}
        onClick={(e) => e.preventDefault()}
        style={{
          backgroundColor: actionButtonBg,
          color: actionButtonColor,
          padding: isMobile ? m.actionBtnPadding : '10px 20px',
          borderRadius: '4px',
          textDecoration: 'none',
          fontWeight: '700',
          fontSize: isMobile ? m.navFontSize : '14px',
          fontFamily,
          whiteSpace: 'nowrap',
          display: 'inline-block',
        }}
      >
        {actionText}
      </a>
    ) : null;

  if (layout === 'action' && !isMobile) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(80px, auto) 1fr minmax(80px, auto)',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
          }}
        >
          <div style={{ justifySelf: 'start' }}>{logoNode}</div>
          <div style={{ justifySelf: 'center' }}>{navLinks}</div>
          <div style={{ justifySelf: 'end' }}>{actionBtn}</div>
        </div>
      </div>
    );
  }

  if (layout === 'action' && isMobile) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center' }}>
        <div style={{ marginBottom: '12px' }}>{logoNode}</div>
        <div style={{ marginBottom: actionBtn ? '12px' : 0 }}>{navLinks}</div>
        {actionBtn}
      </div>
    );
  }

  if (isCentered) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center' }}>
        <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>{logoNode}</div>
        {navLinks}
      </div>
    );
  }

  if (isMobile) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center' }}>
        <div style={{ marginBottom: '12px' }}>{logoNode}</div>
        {navLinks}
      </div>
    );
  }

  return (
    <div
      style={{
        ...containerStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        gap: '16px',
      }}
    >
      <div style={{ flexShrink: 0 }}>{logoNode}</div>
      <div style={{ flexShrink: 0, marginLeft: 'auto' }}>{navLinks}</div>
    </div>
  );
};

const QrCodeBlock: React.FC<{ content: any; styles: any; globalFontFamily?: string }> = ({ content, styles, globalFontFamily }) => {
  const [qrUrl, setQrUrl] = React.useState('');
  const [qrError, setQrError] = React.useState<string | null>(null);
  const text = (content.url || 'https://example.com').trim() || 'https://example.com';
  const fgColor = normalizeHexColorForQr(content.foregroundColor, '#000000');
  const bgColor = normalizeHexColorForQr(content.backgroundColor, '#FFFFFF');
  const width = content.width || 150;
  const align = content.alignment || 'center';

  React.useEffect(() => {
    let cancelled = false;
    setQrError(null);
    setQrUrl('');
    QRCode.toDataURL(text, {
      width: width * 2, // Generate roughly 2x for retina
      margin: 1,
      color: {
        dark: fgColor,
        light: bgColor,
      },
    })
      .then((url) => {
        if (!cancelled) setQrUrl(url);
      })
      .catch((err) => {
        console.error('QR Gen Error:', err);
        if (!cancelled) setQrError('Could not generate QR code for this content.');
      });
    return () => {
      cancelled = true;
    };
  }, [text, fgColor, bgColor, width]);

  const alignStyle: React.CSSProperties = {
    textAlign: align as any,
    display: 'flex',
    flexDirection: 'column',
    alignItems: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
  };

  return (
    <div style={{ ...styles, ...alignStyle }}>
      {qrUrl ? (
        <img
          src={qrUrl}
          alt={`QR code linking to ${text}`}
          style={{ width: `${width}px`, height: 'auto', display: 'block' }}
        />
      ) : qrError ? (
        <div
          style={{
            width: `${width}px`,
            minHeight: `${Math.min(width, 120)}px`,
            padding: '12px',
            background: '#FEF2F2',
            color: '#991B1B',
            fontSize: '12px',
            lineHeight: 1.4,
            borderRadius: '8px',
            border: '1px solid #FECACA',
            boxSizing: 'border-box',
          }}
        >
          {qrError}
        </div>
      ) : (
        <div
          style={{
            width: `${width}px`,
            height: `${width}px`,
            background: '#E2E8F0',
            borderRadius: '4px',
          }}
          title="Generating QR code…"
        />
      )}
    </div>
  );
};

const StarRatingBlock: React.FC<{ content: any; styles: any; globalFontFamily?: string }> = ({ content, styles, globalFontFamily }) => {
  const rating = Math.min(Math.max(0, content.rating || 5), content.maxRating || 5);
  const maxRating = content.maxRating || 5;
  const size = content.size || 24;
  const color = content.color || '#FBBF24';
  const emptyColor = content.emptyColor || '#E5E7EB';
  const align = content.alignment || 'center';

  const alignStyle: React.CSSProperties = {
    textAlign: align as any,
    display: 'flex',
    justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
    gap: '4px',
  };

  return (
    <div style={{ ...styles, ...alignStyle }}>
      {Array.from({ length: maxRating }).map((_, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          fill={i < rating ? color : emptyColor}
          width={size}
          height={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      ))}
    </div>
  );
};

const PollBlock: React.FC<{ content: any; styles: any; globalFontFamily?: string }> = ({ content, styles, globalFontFamily }) => {
  const { question, options = [], pollStyle = 'buttons' } = content;

  return (
    <div style={{ ...styles }}>
      {question && (
        <div style={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '18px' }}>
          {question}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
        {options.map((option: any, index: number) => (
          <a
            key={index}
            href={option.url || '#'}
            onClick={(e) => e.preventDefault()}
            style={{
              display: 'inline-block',
              padding: '10px 20px',
              backgroundColor: '#F3F4F6',
              color: '#1F2937',
              textDecoration: 'none',
              borderRadius: '6px',
              minWidth: '200px',
              textAlign: 'center',
              border: '1px solid #E5E7EB',
              fontWeight: 500,
            }}
          >
            {option.label}
          </a>
        ))}
      </div>
    </div>
  );
};

const AccordionBlock: React.FC<{ content: any; styles: any; globalFontFamily?: string }> = ({ content, styles, globalFontFamily }) => {
  const { items = [] } = content;

  return (
    <div style={{ ...styles }}>
      {items.map((item: any, index: number) => (
        <div key={index} style={{ marginBottom: '8px', border: '1px solid #E5E7EB', borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', backgroundColor: '#F9FAFB', fontWeight: 600, fontSize: '14px', color: '#111827' }}>
            {item.title}
          </div>
          <div style={{ padding: '12px 16px', backgroundColor: '#FFFFFF', fontSize: '14px', color: '#4B5563', borderTop: '1px solid #E5E7EB' }}>
            {item.content}
          </div>
        </div>
      ))}
    </div>
  );
};

const CarouselBlock: React.FC<{ content: any; styles: any; globalFontFamily?: string }> = ({ content, styles, globalFontFamily }) => {
  const { images = [] } = content;
  const [failedSrcs, setFailedSrcs] = React.useState<Set<string>>(new Set());

  return (
    <div
      style={{
        padding: computePadding(styles, '20px'),
        backgroundColor: styles.backgroundColor || 'transparent',
        textAlign: (styles.textAlign as React.CSSProperties['textAlign']) || 'center',
      }}
    >
    <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '10px' }}>
      <div style={{ display: 'inline-flex', gap: '10px' }}>
        {images.map((img: any, index: number) => (
          <div key={`${index}-${img.src || 'empty'}`} style={{
            width: content.carouselImageWidth || '200px',
            height: content.carouselImageHeight || '150px',
            flexShrink: 0,
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #E5E7EB',
            backgroundColor: '#F3F4F6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {img.src && !failedSrcs.has(img.src) ? (
              <img
                src={img.src}
                alt={img.alt}
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: img.objectPosition || 'center center' }}
                onError={() => {
                  setFailedSrcs(prev => new Set(prev).add(img.src));
                }}
              />
            ) : (
              <span style={{ color: '#9CA3AF', fontSize: '12px' }}>{img.src ? 'Image not found' : 'No Image'}</span>
            )}
          </div>
        ))}
      </div>
    </div>
    </div>
  );
};


const GiphyBlock: React.FC<{ content: any; styles: any; globalFontFamily?: string }> = ({ content, styles, globalFontFamily }) => {
  const { src, alt, width = '100%' } = content;

  return (
    <div
      style={{
        padding: computePadding(styles, '20px'),
        backgroundColor: styles.backgroundColor || 'transparent',
        textAlign: (styles.textAlign as React.CSSProperties['textAlign']) || 'center',
      }}
    >
      <img
        src={src}
        alt={alt || 'GIF'}
        style={{ width: width, maxWidth: '100%', height: 'auto', display: 'inline-block' }}
      />
    </div>
  );
};

const VideoBlock: React.FC<{ content: any; styles: any; globalFontFamily?: string }> = ({ content, styles, globalFontFamily }) => {
  const playButtonStyle = content.playButtonStyle || 'circle';
  const thumbnailUrl = resolveVideoThumbnailUrl(content);

  if (!thumbnailUrl) {
    return (
      <div style={{
        ...styles,
        padding: computePadding(styles, '0px'),
        backgroundColor: styles.backgroundColor || 'transparent',
      }}>
        <div style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
          border: '1.5px dashed #CBD5E1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          overflow: 'hidden'
        }}>
          {/* Sublte background play icon */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: 0.05
          }}>
            <svg width="200" height="200" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5V19L19 12L8 5Z" />
            </svg>
          </div>

          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#FFFFFF',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
            zIndex: 1
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#6366F1">
              <path d="M8 5V19L19 12L8 5Z" />
            </svg>
          </div>
          
          <div style={{ textAlign: 'center', zIndex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#1E293B', marginBottom: '2px' }}>Video Placeholder</div>
            <div style={{ fontSize: '13px', color: '#64748B' }}>Add a URL to see your video thumbnail</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles, position: 'relative', cursor: 'pointer', textAlign: styles.textAlign || 'center' }}>
      <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
        <img
          src={thumbnailUrl}
          alt="Video Thumbnail"
          style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '8px' }}
        />
        {playButtonStyle !== 'none' && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60px',
            height: '60px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            borderRadius: playButtonStyle === 'circle' ? '50%' : '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid white'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 5V19L19 12L8 5Z" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export const BlockRenderer: React.FC<BlockRendererProps> = ({
  block,
  isEditable = false,
  onTextUpdate,
  isSelected = false,
  isMobile = false,
  isPalettePreview = false,
  globalFontFamily,
}) => {
  const { type, content } = block;
  const baseStyles = block.styles || {};
  const styles =
    isMobile && block.mobileStyles && Object.keys(block.mobileStyles).length > 0
      ? {
        ...baseStyles,
        ...block.mobileStyles,
      }
      : baseStyles;
  const blockType = type === 'paragraph' ? 'text' : type;

  const renderContent = () => {
    switch (blockType) {
      // ... existing cases ...
      case 'header':
        return <HeaderBlock content={content} styles={styles} globalFontFamily={globalFontFamily} isMobile={isMobile} />;

      case 'menu':
        return <MenuBlock content={content} styles={styles} globalFontFamily={globalFontFamily} isMobile={isMobile} />;

      case 'qr-code':
        return <QrCodeBlock content={content} styles={styles} globalFontFamily={globalFontFamily} />;

      case 'star-rating':
        return <StarRatingBlock content={content} styles={styles} globalFontFamily={globalFontFamily} />;

      case 'poll':
        return <PollBlock content={content} styles={styles} globalFontFamily={globalFontFamily} />;

      case 'accordion':
        return <AccordionBlock content={content} styles={styles} globalFontFamily={globalFontFamily} />;

      case 'image-carousel':
        return <CarouselBlock content={content} styles={styles} globalFontFamily={globalFontFamily} />;

      case 'giphy':
        return <GiphyBlock content={content} styles={styles} globalFontFamily={globalFontFamily} />;

      case 'sticker': {
        const stickerSrc = content.src || '';
        const stickerWidth = content.width || '120px';
        const stickerAlt = content.alt || content.stickerName || 'Sticker';
        const isSvgSticker = isSvgStickerContent(content.stickerType, stickerSrc);

        if (!stickerSrc) {
          return (
            <div
              style={{
                padding: computePadding(styles, '20px'),
                textAlign: (styles.textAlign as any) || 'center',
                backgroundColor: styles.backgroundColor || 'transparent',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '160px',
                  height: '140px',
                  backgroundColor: '#F8FAFC',
                  borderRadius: '16px',
                  border: '2px dashed #CBD5E1',
                  color: '#94A3B8',
                }}
              >
                <span style={{ fontSize: '40px', marginBottom: '8px' }}>🩹</span>
                <span style={{ fontSize: '13px', fontWeight: 500 }}>Choose a sticker</span>
              </div>
            </div>
          );
        }

        return (
          <div
            style={{
              padding: computePadding(styles, '10px'),
              textAlign: (styles.textAlign as any) || 'center',
              backgroundColor: styles.backgroundColor || 'transparent',
            }}
          >
            {isSvgSticker ? (
              <div
                style={{
                  display: 'inline-block',
                  width: stickerWidth,
                  height: 'auto',
                }}
                dangerouslySetInnerHTML={{ __html: stickerSrc }}
              />
            ) : (
              <img
                src={stickerSrc}
                alt={stickerAlt}
                style={{
                  width: stickerWidth,
                  height: 'auto',
                  display: 'inline-block',
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            )}
          </div>
        );
      }

      case 'magic-text':
      case 'text': {
        let textValue: string = (typeof content.text === 'string' ? content.text : String(content.text || '')).trim();
        const listType = content.listType as 'unordered' | 'ordered' | undefined;

        // Compute standardized padding for text blocks
        const textPadding = computePadding(styles, '20px');

        const baseStyle: React.CSSProperties = {
          display: 'block',
          width: '100%',
          boxSizing: 'border-box',
          margin: 0,
          padding: textPadding,
          fontSize: styles.fontSize || '16px',
          fontWeight: styles.fontWeight || '400',
          fontStyle: styles.fontStyle || 'normal',
          fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
          lineHeight: styles.lineHeight || '150%',
          letterSpacing: styles.letterSpacing || '0px',
          color: styles.color || '#111827',
          textAlign: styles.textAlign || 'left',
          textDecoration: styles.textDecoration || 'none',
          backgroundColor: styles.backgroundColor || 'transparent',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          maxWidth: '100%',
        };

        const isHtmlContent = Boolean(content.isHtml);
        if (isHtmlContent) {
          return (
            <div
              style={baseStyle}
              dangerouslySetInnerHTML={{ __html: textValue }}
            />
          );
        }

        // All whitespace has been removed, so lengths are 0
        const leadingWhitespaceLength = 0;
        const trailingWhitespaceLength = 0;

        const hasLink =
          Boolean(content.linkUrl && content.linkUrl.trim()) &&
          typeof content.linkRangeStart === 'number' &&
          typeof content.linkRangeEnd === 'number' &&
          content.linkRangeEnd > content.linkRangeStart;
        const linkStart =
          typeof content.linkRangeStart === 'number' ? Math.max(0, content.linkRangeStart - leadingWhitespaceLength) : 0;
        const linkEnd =
          typeof content.linkRangeEnd === 'number'
            ? Math.min(textValue.length, Math.max(0, content.linkRangeEnd - leadingWhitespaceLength))
            : textValue.length;

        // Adjust color ranges to account for trimmed leading and trailing whitespace
        const adjustedColorRanges: TextColorRange[] | undefined = content.colorRanges
          ? (content.colorRanges as TextColorRange[]).map((range) => ({
            start: Math.max(0, range.start - leadingWhitespaceLength),
            end: Math.min(textValue.length, Math.max(0, range.end - leadingWhitespaceLength)),
            color: range.color,
          })).filter((range) => range.end > range.start && range.start < textValue.length)
          : undefined;

        const colorRanges = normalizeColorRanges(
            adjustedColorRanges,
            textValue.length
        );

        const renderSegment = (start: number, end: number): React.ReactNode => {
          if (start >= end) return null;
          const boundaries = new Set<number>([start, end]);
          if (hasLink && linkEnd > linkStart) {
            const linkStartInRange = Math.max(start, Math.min(linkStart, end));
            const linkEndInRange = Math.max(linkStartInRange, Math.min(linkEnd, end));
            if (linkEndInRange > linkStartInRange) {
              boundaries.add(linkStartInRange);
              boundaries.add(linkEndInRange);
            }
          }
          colorRanges.forEach((range) => {
            const rangeStart = Math.max(start, Math.min(range.start, end));
            const rangeEnd = Math.max(rangeStart, Math.min(range.end, end));
            if (rangeEnd > rangeStart) {
              boundaries.add(rangeStart);
              boundaries.add(rangeEnd);
            }
          });
          const sorted = Array.from(boundaries).sort((a, b) => a - b);
          const nodes: React.ReactNode[] = [];
          for (let i = 0; i < sorted.length - 1; i += 1) {
            const segStart = sorted[i];
            const segEnd = sorted[i + 1];
            if (segEnd <= segStart) continue;
            const segmentText = textValue.slice(segStart, segEnd);
            if (!segmentText) continue;
            const appliedColor = colorRanges.find(
              (range) => segStart >= range.start && segStart < range.end
            )?.color;
            const isLinkSegment =
              hasLink &&
              segEnd > linkStart &&
              segStart < linkEnd;
            let chunkNode: React.ReactNode = segmentText;
            if (isLinkSegment) {
              chunkNode = (
                <a
                  key={`link-${segStart}-${segEnd}`}
                  href={content.linkUrl}
                  target={content.linkTarget || '_blank'}
                  rel="noopener noreferrer"
                  onClick={(event) => event.preventDefault()}
                  style={{
                    color: appliedColor || styles.color || '#111827',
                    textDecoration: styles.textDecoration || 'underline',
                  }}
                >
                  {segmentText || content.linkUrl}
                </a>
              );
            } else if (appliedColor) {
              chunkNode = (
                <span style={{ color: appliedColor }} key={`color-${segStart}-${segEnd}`}>
                  {segmentText}
                </span>
              );
            }
            nodes.push(
              <React.Fragment key={`seg-${segStart}-${segEnd}-${appliedColor || 'base'}`}>
                {chunkNode}
              </React.Fragment>
            );
          }
          if (!nodes.length) {
            return textValue.slice(start, end) || '\u00A0';
          }
          return nodes;
        };

        if (listType === 'unordered' || listType === 'ordered') {
          const lines: string[] = textValue.split('\n');
          let cursor = 0;
          const ListTag = (listType === 'ordered' ? 'ol' : 'ul') as keyof React.JSX.IntrinsicElements;
          const listStyleType = listType === 'ordered' ? 'decimal' : 'disc';
          return (
            <div style={baseStyle}>
              <ListTag
                style={{
                  margin: 0,
                  paddingLeft: '1.5rem',
                  listStylePosition: 'outside',
                  listStyleType,
                }}
              >
                {lines.map((line, index) => {
                  const start = cursor;
                  const end = start + line.length;
                  cursor = end + 1;
                  return (
                    <li key={`${line}-${index}`} style={{ marginBottom: '0.35em' }}>
                      {renderSegment(start, end)}
                    </li>
                  );
                })}
              </ListTag>
            </div>
          );
        }

        // If editable and no link, make it contentEditable
        if (isEditable && !hasLink && !listType) {
          return (
            <div
              contentEditable
              suppressContentEditableWarning
              onClick={(e) => {
                e.stopPropagation();
              }}
              onBlur={(e) => {
                const newText = e.currentTarget.textContent || '';
                onTextUpdate?.(newText);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
                if (e.key === 'Escape') {
                  e.currentTarget.blur();
                }
              }}
              style={{
                ...baseStyle,
                whiteSpace: 'pre-wrap',
                outline: isSelected ? 'none' : '2px solid #3B82F6',
                outlineOffset: isSelected ? '0' : '2px',
                borderRadius: '4px',
                cursor: 'text',
                minHeight: '1.5em',
              }}
            >
              {textValue}
            </div>
          );
        }

        return (
          <div
            style={{
              ...baseStyle,
              whiteSpace: 'pre-wrap',
            }}
          >
            {renderSegment(0, textValue.length)}
          </div>
        );
      }

      case 'heading': {
        const level = content.level || 1;
        const headingText = content.text || 'Heading';
        const headingPosition = styles.headingPosition;

        const superscriptFontSize = (() => {
          const value = styles.fontSize || '32px';
          if (typeof value === 'number') return `${value * 0.75}px`;
          const parsed = parseFloat(value);
          if (!Number.isNaN(parsed)) {
            const unit = value.replace(parsed.toString(), '') || 'px';
            return `${parsed * 0.75}${unit}`;
          }
          return '0.75em';
        })();

        const subscriptFontSize = (() => {
          const value = styles.fontSize || '32px';
          if (typeof value === 'number') return `${value * 0.8}px`;
          const parsed = parseFloat(value);
          if (!Number.isNaN(parsed)) {
            const unit = value.replace(parsed.toString(), '') || 'px';
            return `${parsed * 0.8}${unit}`;
          }
          return '0.8em';
        })();

        const sanitizeRange = (text: string, start?: number, end?: number) => {
          const length = text.length;
          const rawStart = typeof start === 'number' ? start : 0;
          const rawEnd = typeof end === 'number' ? end : length;
          const safeStart = Math.max(0, Math.min(rawStart, length));
          const safeEnd = Math.max(safeStart, Math.min(rawEnd, length));
          return { start: safeStart, end: safeEnd };
        };

        const { start: linkStart, end: linkEnd } = sanitizeRange(
          headingText,
          content.linkRangeStart,
          content.linkRangeEnd
        );
        const hasLinkedText =
          Boolean(content.linkUrl && content.linkUrl.trim()) && linkEnd > linkStart;

        const ensureUnderline = (decoration?: string) => {
          if (!decoration) return 'underline';
          const tokens = decoration
            .split(/\s+/)
            .map((token) => token.trim())
            .filter(Boolean);
          if (!tokens.includes('underline')) {
            tokens.push('underline');
          }
          return tokens.join(' ');
        };

        const linkedTextDecoration = ensureUnderline(styles.textDecoration);

        const colorRanges = normalizeColorRanges(
          content.colorRanges as TextColorRange[] | undefined,
          headingText.length
        );

        const renderLinkedContent = () => {
          if (!headingText.length) return null;
          const boundaries = new Set<number>([0, headingText.length]);
          if (hasLinkedText && linkEnd > linkStart) {
            boundaries.add(linkStart);
            boundaries.add(linkEnd);
          }
          colorRanges.forEach((range) => {
            const start = Math.max(0, Math.min(range.start, headingText.length));
            const end = Math.max(start, Math.min(range.end, headingText.length));
            if (end > start) {
              boundaries.add(start);
              boundaries.add(end);
            }
          });
          const sorted = Array.from(boundaries).sort((a, b) => a - b);
          const nodes: React.ReactNode[] = [];
          for (let i = 0; i < sorted.length - 1; i += 1) {
            const segStart = sorted[i];
            const segEnd = sorted[i + 1];
            if (segEnd <= segStart) continue;
            const segmentText = headingText.slice(segStart, segEnd);
            if (!segmentText) continue;
            const colorValue = colorRanges.find(
              (range) => segStart >= range.start && segStart < range.end
            )?.color;
            const isLinkSegment =
              hasLinkedText &&
              segEnd > linkStart &&
              segStart < linkEnd;
            let chunkNode: React.ReactNode = segmentText;
            if (isLinkSegment) {
              chunkNode = (
                <a
                  key={`heading-link-${segStart}-${segEnd}`}
                  href={content.linkUrl}
                  target={content.linkTarget || '_blank'}
                  rel="noopener noreferrer"
                  onClick={(event) => event.preventDefault()}
                  style={{
                    color: colorValue || styles.color || '#111827',
                    textDecoration: linkedTextDecoration,
                    display: 'inline-block',
                  }}
                >
                  {segmentText || content.linkUrl}
                </a>
              );
            } else if (colorValue) {
              chunkNode = (
                <span style={{ color: colorValue }} key={`heading-color-${segStart}-${segEnd}`}>
                  {segmentText}
                </span>
              );
            }
            nodes.push(
              <React.Fragment key={`heading-seg-${segStart}-${segEnd}`}>
                {chunkNode}
              </React.Fragment>
            );
          }
          return nodes.length ? nodes : headingText;
        };

        const renderHeadingInner = () => {
          const baseContent = renderLinkedContent();
          if (headingPosition === 'superscript') {
            return (
              <sup
                style={{
                  fontSize: superscriptFontSize,
                  lineHeight: '1',
                  display: 'inline-block',
                  transform: 'translateY(-0.35em)',
                }}
              >
                {baseContent}
              </sup>
            );
          }
          if (headingPosition === 'subscript') {
            return (
              <sub
                style={{
                  fontSize: subscriptFontSize,
                  lineHeight: '1',
                  display: 'inline-block',
                  transform: 'translateY(0.2em)',
                }}
              >
                {baseContent}
              </sub>
            );
          }
          return baseContent;
        };

        // Compute standardized padding for headings (default 20px aligns with text blocks)
        const headingPadding = computePadding(styles, '20px');

        const headingProps = {
          style: {
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
            padding: headingPadding,
            fontSize: styles.fontSize || '32px',
            fontWeight: styles.fontWeight || '700',
            fontStyle: styles.fontStyle || 'normal',
            color: styles.color || '#111827',
            textAlign: styles.textAlign || 'left',
            lineHeight: styles.lineHeight || '140%',
            letterSpacing: styles.letterSpacing || '0px',
            fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
            textDecoration: styles.textDecoration || 'none',
            margin: '0',
            backgroundColor: styles.backgroundColor || 'transparent',
            wordWrap: 'break-word' as React.CSSProperties['wordWrap'],
            overflowWrap: 'break-word' as React.CSSProperties['overflowWrap'],
            maxWidth: '100%',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          } as React.CSSProperties,
          children: renderHeadingInner(),
        };

        if (level === 1) return <h1 {...headingProps} />;
        if (level === 2) return <h2 {...headingProps} />;
        if (level === 3) return <h3 {...headingProps} />;
        if (level === 4) return <h4 {...headingProps} />;
        return <h1 {...headingProps} />;
      }

      case 'title': {
        const titleText = content.text || 'Display Title';
        return (
          <h1
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              padding: computePadding(styles, '30px 20px 20px 20px'),
              fontSize: styles.fontSize || '48px',
              fontWeight: styles.fontWeight || '800',
              color: styles.color || '#0F172A',
              textAlign: styles.textAlign || 'left',
              letterSpacing: styles.letterSpacing || '-0.025em',
              lineHeight: styles.lineHeight || '110%',
              fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              margin: '0',
              backgroundColor: styles.backgroundColor || 'transparent',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {titleText}
          </h1>
        );
      }

      case 'subtitle': {
        const subtitleText = content.text || 'Supporting subtitle text';
        return (
          <h2
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              padding: computePadding(styles, '10px 20px 20px 20px'),
              fontSize: styles.fontSize || '20px',
              fontWeight: styles.fontWeight || '500',
              color: styles.color || '#64748B',
              textAlign: styles.textAlign || 'left',
              lineHeight: styles.lineHeight || '150%',
              fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              margin: '0',
              backgroundColor: styles.backgroundColor || 'transparent',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {subtitleText}
          </h2>
        );
      }

      case 'quote': {
        const quoteText = content.text || '"A beautiful quote to inspire your readers."';
        const author = content.author || '— Author Name';
        return (
          <blockquote
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              padding: computePadding(styles, '20px'),
              fontSize: styles.fontSize || '20px',
              fontWeight: styles.fontWeight || '400',
              color: styles.color || '#374151',
              textAlign: styles.textAlign || 'center',
              fontStyle: styles.fontStyle || 'italic',
              lineHeight: styles.lineHeight || '160%',
              borderLeft: styles.borderLeft || '4px solid #3B82F6',
              backgroundColor: styles.backgroundColor || '#F8FAFC',
              fontFamily: styles.fontFamily || 'Georgia, serif',
              margin: '0',
            }}
          >
            <p style={{ margin: '0 0 12px 0' }}>{quoteText}</p>
            {author && (
              <cite style={{ fontSize: '14px', fontStyle: 'normal', color: '#6B7280' }}>
                {author}
              </cite>
            )}
          </blockquote>
        );
      }

      case 'caption': {
        const captionText = content.text || 'Caption text for images or descriptions';
        return (
          <p
            style={{
              padding: computePadding(styles, '8px 20px'),
              fontSize: styles.fontSize || '12px',
              fontWeight: styles.fontWeight || '400',
              color: styles.color || '#9CA3AF',
              textAlign: styles.textAlign || 'center',
              lineHeight: styles.lineHeight || '140%',
              fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              margin: '0',
              backgroundColor: styles.backgroundColor || 'transparent',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {captionText}
          </p>
        );
      }

      case 'preheader': {
        const preheaderText = content.text || 'Preview text for email inbox';
        return (
          <div
            style={{
              padding: computePadding(styles, '10px 20px'),
              fontSize: styles.fontSize || '14px',
              fontWeight: styles.fontWeight || '400',
              color: styles.color || '#6B7280',
              textAlign: styles.textAlign || 'left',
              lineHeight: styles.lineHeight || '150%',
              fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              backgroundColor: styles.backgroundColor || '#F9FAFB',
              borderLeft: '3px solid #D1D5DB',
              margin: '0',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <span style={{ fontSize: '10px', color: '#9CA3AF', display: 'block', marginBottom: '4px' }}>
              PREHEADER TEXT
            </span>
            {preheaderText}
          </div>
        );
      }

      case 'list': {
        const items = Array.isArray(content.items)
          ? content.items
          : (typeof content.text === 'string' && content.text.trim()
            ? content.text.split('\n').filter(line => line.trim())
            : ['List item 1', 'List item 2', 'List item 3']);

        const listType = content.listType === 'number' || content.listType === 'ordered' ? 'ordered' : (content.listType === 'check' ? 'check' : (content.listType === 'none' ? 'none' : 'unordered'));
        const ListComponent = listType === 'ordered' ? 'ol' : 'ul';
        const textAlign = styles.textAlign || 'left';
        const isCentered = textAlign === 'center';
        const isRight = textAlign === 'right';

        return (
          <ListComponent
            style={{
              padding: computePadding(styles, '20px'),
              fontSize: styles.fontSize || '16px',
              color: styles.color || '#374151',
              textAlign: textAlign,
              lineHeight: styles.lineHeight || '160%',
              fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              backgroundColor: styles.backgroundColor || 'transparent',
              margin: '0',
              listStyleType: 'none',
              paddingLeft: '0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: isCentered ? 'center' : (isRight ? 'flex-end' : 'flex-start'),
            }}
          >
            {items.map((item: string, index: number) => (
              <li
                key={index}
                style={{
                  marginBottom: styles.listItemSpacing || '8px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: isCentered ? 'center' : (isRight ? 'flex-end' : 'flex-start'),
                  width: '100%',
                }}
              >
                {listType !== 'none' && (
                  <div
                    style={{
                      width: '40px',
                      textAlign: 'right',
                      paddingRight: '12px',
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'flex-end',
                      color: styles.markerColor || styles.color || '#94A3B8',
                      fontSize: '0.9em',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {listType === 'check' ? (
                      <span style={{ color: styles.markerColor || styles.color || '#000', fontWeight: 'bold' }}>✓</span>
                    ) : listType === 'ordered' ? (
                      <span>{index + 1}.</span>
                    ) : (
                      <span>•</span>
                    )}
                  </div>
                )}
                <span>{item}</span>
              </li>
            ))}
          </ListComponent>
        );
      }

      case 'image':
        const imageSrc = content.src || '';
        const hasImage = Boolean(imageSrc && imageSrc.trim() !== '');
        const isAutoWidth = content.autoWidth !== false;
        const placeholderHeight = styles.minHeight || content.height || '400px';
        const minHeight = hasImage
          ? content.height && !isAutoWidth
            ? content.height
            : 'auto'
          : placeholderHeight;
        const linkHref = buildActionHref(content.linkType, content.linkUrl);
        const hasLink = Boolean(linkHref);

        const imageShape = content.imageShape || 'square';
        const shapeStyles: React.CSSProperties = {
          borderRadius:
            imageShape === 'circle' ? '50%' :
              imageShape === 'arch' ? '100px 100px 0 0' :
                imageShape === 'shield' ? '0 0 100px 100px' :
                  imageShape === 'rounded' ? '24px' :
                    imageShape === 'leaf' ? '50px 0 50px 0' :
                      imageShape === 'blob' ? '60% 40% 30% 70% / 60% 30% 70% 40%' :
                        imageShape === 'badge' ? '24px 24px 24px 0' :
                          imageShape === 'pill' ? '100px' : '0',
          clipPath:
            imageShape === 'clover' ? 'polygon(50% 0%, 80% 10%, 100% 35%, 100% 70%, 80% 90%, 50% 100%, 20% 90%, 0% 70%, 0% 35%, 20% 10%)' :
              imageShape === 'star-8' ? 'polygon(50% 0%, 63% 38%, 100% 50%, 63% 62%, 50% 100%, 37% 62%, 0% 50%, 37% 38%)' :
                imageShape === 'burst' ? 'polygon(50% 0%, 61% 35%, 82% 18%, 75% 44%, 100% 50%, 75% 56%, 82% 82%, 61% 65%, 50% 100%, 39% 65%, 18% 82%, 25% 56%, 0% 50%, 25% 44%, 18% 18%, 39% 35%)' :
                  imageShape === 'sun' ? 'polygon(50% 0%, 64% 19%, 88% 12%, 81% 36%, 100% 50%, 81% 64%, 88% 88%, 64% 81%, 50% 100%, 36% 81%, 12% 88%, 19% 64%, 0% 50%, 19% 36%, 12% 12%, 36% 19%)' : 'none',
          aspectRatio: 'auto',
          objectFit: 'cover',
        };

        const imageElement = hasImage ? (
          <img
            src={imageSrc}
            alt={content.alt || 'Image'}
            style={{
              width: isAutoWidth ? 'auto' : content.width || '100%',
              height: content.height || 'auto',
              minHeight: minHeight,
              maxWidth: '100%',
              display: 'block',
              marginLeft: (styles.textAlign === 'left') ? '0' : (styles.textAlign === 'right') ? 'auto' : 'auto',
              marginRight: (styles.textAlign === 'right') ? '0' : (styles.textAlign === 'left') ? 'auto' : 'auto',
              verticalAlign: 'middle',
              ...shapeStyles,
            }}
          />
        ) : (
          <div
            style={{
              width: isAutoWidth ? '100%' : content.width || '100%',
              margin: '0 auto',
              minHeight: minHeight,
              backgroundColor: '#F9FAFB',
              border: '2px dashed #CBD5E1',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748B',
              position: 'relative',
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(203, 213, 225, 0.1) 10px, rgba(203, 213, 225, 0.1) 20px)',
              ...shapeStyles,
            }}
          >
            <svg
              style={{
                width: '64px',
                height: '64px',
                marginBottom: '12px',
                opacity: 0.6,
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <div style={{ fontSize: '14px', fontWeight: '500' }}>Image Placeholder</div>
          </div>
        );

        // Use individual padding sides if available, otherwise use general padding
        const imagePadding = (styles.paddingTop || styles.paddingBottom || styles.paddingLeft || styles.paddingRight)
          ? `${styles.paddingTop || '0px'} ${styles.paddingRight || '0px'} ${styles.paddingBottom || '0px'} ${styles.paddingLeft || '0px'}`
          : computePadding(styles, '20px');
        // Sticker overlay logic
        const overlayStickerData = content.overlaySticker;
        const hasOverlaySticker = overlayStickerData?.src;
        const overlayTextData = content.overlayText || {};
        const hasOverlayText =
          Boolean(overlayTextData.enabled) &&
          (Boolean((overlayTextData.headline || '').trim()) || Boolean((overlayTextData.subtext || '').trim()));

        const getOverlayPositionStyles = (position: string): React.CSSProperties => {
          const base: React.CSSProperties = { position: 'absolute', zIndex: 10, pointerEvents: 'none' };
          const offset = '6%';
          switch (position) {
            case 'top-left': return { ...base, top: offset, left: offset };
            case 'top-center': return { ...base, top: offset, left: '50%', transform: 'translateX(-50%)' };
            case 'top-right': return { ...base, top: offset, right: offset };
            case 'center-left': return { ...base, top: '50%', left: offset, transform: 'translateY(-50%)' };
            case 'center': return { ...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
            case 'center-right': return { ...base, top: '50%', right: offset, transform: 'translateY(-50%)' };
            case 'bottom-left': return { ...base, bottom: offset, left: offset };
            case 'bottom-center': return { ...base, bottom: offset, left: '50%', transform: 'translateX(-50%)' };
            case 'bottom-right': return { ...base, bottom: offset, right: offset };
            default: return { ...base, top: offset, right: offset };
          }
        };

        const overlayStickerElement = hasOverlaySticker ? (() => {
          const stickerSize = `${overlayStickerData.size || 25}%`;
          const posStyles = getOverlayPositionStyles(overlayStickerData.position || 'top-right');
          const isSvg = overlayStickerData.stickerType === 'builtin' && overlayStickerData.src.startsWith('<svg');
          const rotation = overlayStickerData.rotation || 0;
          const rotateStr = rotation ? ` rotate(${rotation}deg)` : '';
          const combinedTransform = ((posStyles.transform || '') + rotateStr).trim() || undefined;

          return (
            <div style={{ ...posStyles, transform: combinedTransform, width: stickerSize, maxWidth: '150px' }}>
              {isSvg ? (
                <div
                  style={{ width: '100%', height: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: overlayStickerData.src }}
                />
              ) : (
                <img
                  src={overlayStickerData.src}
                  alt={overlayStickerData.stickerName || 'Sticker'}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              )}
            </div>
          );
        })() : null;

        const overlayTextElement = hasOverlayText ? (() => {
          const headline = (overlayTextData.headline || '').trim();
          const subtext = (overlayTextData.subtext || '').trim();
          const position = overlayTextData.position || 'center';
          const align = overlayTextData.align || 'center';
          const getTextPositionStyles = (): React.CSSProperties => {
            const base: React.CSSProperties = {
              position: 'absolute',
              zIndex: 12,
              left: '8%',
              right: '8%',
              pointerEvents: 'none',
            };
            if (position === 'top') return { ...base, top: '10%' };
            if (position === 'bottom') return { ...base, bottom: '10%' };
            return { ...base, top: '50%', transform: 'translateY(-50%)' };
          };
          const textShadow =
            overlayTextData.textShadow === false
              ? 'none'
              : '0 2px 8px rgba(0,0,0,0.35)';
          const overlayFontFamily = formatFontFamilyForRender(
            overlayTextData.fontFamily || styles.fontFamily || globalFontFamily,
            'Arial, sans-serif'
          );
          return (
            <div style={getTextPositionStyles()}>
              {headline ? (
                <div
                  style={{
                    fontSize: `${overlayTextData.headlineSize || 56}px`,
                    fontWeight: overlayTextData.headlineWeight || 800,
                    fontFamily: overlayFontFamily,
                    color: overlayTextData.headlineColor || '#D9FF4A',
                    lineHeight: 1.05,
                    letterSpacing: overlayTextData.headlineLetterSpacing || '0px',
                    textAlign: align,
                    textShadow,
                    whiteSpace: 'pre-wrap',
                    marginBottom: subtext ? '14px' : 0,
                  }}
                >
                  {headline}
                </div>
              ) : null}
              {subtext ? (
                <div
                  style={{
                    fontSize: `${overlayTextData.subtextSize || 18}px`,
                    fontWeight: overlayTextData.subtextWeight || 500,
                    fontFamily: overlayFontFamily,
                    color: overlayTextData.subtextColor || '#FFFFFF',
                    lineHeight: 1.45,
                    letterSpacing: overlayTextData.subtextLetterSpacing || '0px',
                    textAlign: align,
                    textShadow,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {subtext}
                </div>
              ) : null}
            </div>
          );
        })() : null;

        return (
          <div
            style={{
              padding: imagePadding,
              textAlign: styles.textAlign || 'center',
              minHeight: minHeight,
              position: 'relative',
              backgroundColor: styles.backgroundColor || 'transparent',
              lineHeight: 0,
            }}
          >
            <div style={{ position: 'relative', display: hasImage ? 'inline-block' : 'block', maxWidth: '100%', lineHeight: 0, fontSize: 0 }}>
              {hasLink ? (
                <a
                  href={linkHref}
                  target={content.linkTarget || '_blank'}
                  rel={content.linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                  }}
                  style={{
                    display: 'block',
                    textDecoration: 'none',
                    cursor: 'default',
                  }}
                >
                  {imageElement}
                </a>
              ) : (
                imageElement
              )}
              {overlayTextElement}
              {overlayStickerElement}
            </div>
          </div>
        );

      case 'button': {
        const buttonStyle = styles.buttonStyle || {};
        const variant = buttonStyle.variant || 'solid';
        const backgroundColor = buttonStyle.backgroundColor || '#3B82F6';
        const textColor = buttonStyle.color || '#FFFFFF';
        const borderRadius = buttonStyle.borderRadius || '6px';
        const widthValue = buttonStyle.width || 'auto';
        const borderColor = buttonStyle.borderColor || textColor || backgroundColor;
        const paddingValue = buttonStyle.padding || '12px 24px';
        const buttonFontFamily = buttonStyle.fontFamily || styles.fontFamily || 'Arial, sans-serif';
        const buttonFontSize = buttonStyle.fontSize || '16px';
        const buttonFontWeight = buttonStyle.fontWeight || '600';
        const textTransform = buttonStyle.textTransform || 'none';
        const letterSpacing = buttonStyle.letterSpacing || '0px';
        const shadowStyle =
          buttonStyle.shadow === 'soft'
            ? '0 10px 25px rgba(15, 23, 42, 0.25)'
            : buttonStyle.shadow === 'hard'
              ? '0 4px 12px rgba(15, 23, 42, 0.35)'
              : 'none';

        const resolveBackground = () => {
          if (variant === 'gradient') {
            return (
              buttonStyle.gradient ||
              `linear-gradient(135deg, ${backgroundColor}, #7C3AED)`
            );
          }
          if (variant === 'outline' || variant === 'ghost') {
            return 'transparent';
          }
          return backgroundColor;
        };

        const resolveBorder = () => {
          if (variant === 'outline') {
            return `2px solid ${borderColor}`;
          }
          if (variant === 'ghost') {
            return '1px solid transparent';
          }
          return '1px solid transparent';
        };

        const resolveTextColor = () => {
          if (variant === 'outline' || variant === 'ghost') {
            return textColor || backgroundColor;
          }
          return textColor;
        };

        const textAlign = styles.textAlign || 'center';
        const justifyContent =
          textAlign === 'left' ? 'flex-start' :
            textAlign === 'right' ? 'flex-end' :
              'center';

        const actionType = (content.actionType as 'open-url' | 'download-file') || 'open-url';
        const isDownloadAction = actionType === 'download-file';
        const buttonHref = isDownloadAction
          ? (content.downloadFileUrl && content.downloadFileUrl.trim()) || '#'
          : buildActionHref(content.actionType, content.url) || '#';
        const buttonTarget = isDownloadAction ? '_self' : content.target || '_blank';
        const relAttr = buttonTarget === '_blank' ? 'noopener noreferrer' : undefined;
        const downloadAttr = isDownloadAction
          ? content.downloadAs || content.downloadFileName || undefined
          : undefined;

        return (
          <div
            style={{
              padding: computePadding(styles, '20px'),
              display: 'flex',
              justifyContent: justifyContent,
              textAlign: textAlign as React.CSSProperties['textAlign'],
              backgroundColor: styles.backgroundColor || 'transparent',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <a
              href={buttonHref}
              target={buttonTarget}
              rel={relAttr}
              download={downloadAttr}
              onClick={(event) => event.preventDefault()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4em',
                width: widthValue === 'auto' ? undefined : widthValue,
                minWidth: widthValue === 'auto' ? undefined : '0',
                maxWidth: '100%',
                flexShrink: widthValue === 'auto' ? 1 : 0,
                flexGrow: widthValue === 'auto' ? 0 : 0,
                textAlign: 'center',
                background: resolveBackground(),
                color: resolveTextColor(),
                padding: paddingValue,
                borderRadius,
                fontSize: buttonFontSize,
                fontWeight: buttonFontWeight,
                fontFamily: buttonFontFamily,
                textDecoration: 'none',
                border: resolveBorder(),
                textTransform,
                letterSpacing,
                boxShadow: shadowStyle,
                transition: 'transform 160ms ease, box-shadow 160ms ease',
              }}
              onMouseEnter={(e) => {
                if (buttonStyle.hoverScale === false) return;
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                if (buttonStyle.hoverScale === false) return;
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
              }}
            >
              {content.text || 'Button'}
            </a>
          </div>
        );
      }

      case 'divider':
        return (
          <div
            style={{
              padding: computePadding(styles, '20px'),
              backgroundColor: styles.backgroundColor || 'transparent',
            }}
          >
            <hr
              style={{
                border: 'none',
                borderTop: `${styles.borderWidth || '1px'} ${styles.borderStyle || 'solid'} ${styles.borderColor || '#E5E7EB'
                  }`,
                margin: '0',
              }}
            />
          </div>
        );

      case 'spacer':
        return (
          <div
            style={{
              height: content.height || '40px',
              padding: styles.padding || '0px',
              backgroundColor: styles.backgroundColor || 'transparent',
            }}
          />
        );

      case 'hero':
        // Use responsive padding - smaller on mobile
        const heroPadding = computePadding(styles, isMobile ? '32px 20px' : '60px 20px');
        return (
          <div
            style={{
              padding: heroPadding,
              textAlign: styles.textAlign || 'center',
              backgroundColor: styles.backgroundColor || '#F8FAFC',
              backgroundImage: content.backgroundImage
                ? `url(${content.backgroundImage})`
                : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <h1
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: styles.color || '#111827',
                margin: '0 0 16px',
              }}
            >
              {content.title || 'Welcome to Our Newsletter'}
            </h1>
            {content.subtitle && (
              <p
                style={{
                  fontSize: '18px',
                  color: '#6B7280',
                  margin: '0 0 24px',
                }}
              >
                {content.subtitle}
              </p>
            )}
            {content.buttonText && (
              <a
                href={content.buttonUrl || '#'}
                style={{
                  display: 'inline-block',
                  backgroundColor: '#3B82F6',
                  color: '#FFFFFF',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  textDecoration: 'none',
                }}
              >
                {content.buttonText}
              </a>
            )}
          </div>
        );

      case 'columns':
        const columnCount = content.columns || 2;
        const columnWidths = content.columnWidths || [];
        const hasCustomWidths = columnWidths.length === columnCount;
        const defaultColumnWidth = `${100 / columnCount}%`;

        // Calculate maximum number of blocks across all columns for grid alignment
        const maxBlocksPerColumn = Math.max(
          ...Array.from({ length: columnCount }).map((_, idx) => {
            const colContent = content.columnContent?.[idx];
            if (Array.isArray(colContent)) {
              return colContent.filter(item => item && item !== '').length;
            }
            return colContent && colContent !== '' ? 1 : 0;
          }),
          1
        );

        const renderColumnContent = (columnItem: any, index: number) => {
          // Handle empty column
          if (!columnItem || columnItem === '' || (Array.isArray(columnItem) && columnItem.length === 0)) {
            return Array.from({ length: maxBlocksPerColumn }).map((_, blockIdx) => (
              <div
                key={blockIdx}
                style={{
                  border: blockIdx === 0 ? '1px dashed #E5E7EB' : 'none',
                  borderRadius: '6px',
                  minHeight: blockIdx === 0 ? '160px' : '0px',
                  backgroundColor: blockIdx === 0 ? '#F9FAFB' : 'transparent',
                }}
              />
            ));
          }

          // Handle array of blocks (new format)
          if (Array.isArray(columnItem)) {
            const blocks = columnItem.filter(item => item && item !== '');
            return Array.from({ length: maxBlocksPerColumn }).map((_, blockIndex) => {
              const blockItem = blocks[blockIndex];
              if (!blockItem) {
                return <div key={blockIndex} style={{ minHeight: '0px' }} />;
              }

              return (
                <div key={blockIndex} style={{ flexShrink: 0 }}>
                  <BlockRenderer
                    block={{
                      id: `${block.id}-col-${index}-block-${blockIndex}`,
                      type: blockItem.type,
                      content: blockItem.content || {},
                      styles: blockItem.styles || {},
                    }}
                    isEditable={isEditable}
                    onTextUpdate={onTextUpdate}
                    isSelected={isSelected}
                    isMobile={isMobile}
                  />
                </div>
              );
            });
          }

          // Handle legacy string format
          if (typeof columnItem === 'string') {
            return Array.from({ length: maxBlocksPerColumn }).map((_, blockIdx) => (
              <div key={blockIdx}>
                {blockIdx === 0 ? columnItem : null}
              </div>
            ));
          }

          // Handle single block object (legacy format)
          if (columnItem.type) {
            return Array.from({ length: maxBlocksPerColumn }).map((_, blockIdx) => (
              <div key={blockIdx}>
                {blockIdx === 0 ? (
                  <BlockRenderer
                    block={{
                      id: `${block.id}-col-${index}`,
                      type: columnItem.type,
                      content: columnItem.content || {},
                      styles: columnItem.styles || {},
                    }}
                    isEditable={isEditable}
                    onTextUpdate={onTextUpdate}
                    isSelected={isSelected}
                    isMobile={isMobile}
                  />
                ) : null}
              </div>
            ));
          }

          return Array.from({ length: maxBlocksPerColumn }).map((_, blockIdx) => (
            <div key={blockIdx}>{blockIdx === 0 ? (columnItem.content || null) : null}</div>
          ));
        };

        // Use smaller gaps on mobile for more content space
        const gapSize = isMobile ? (hasCustomWidths ? 8 : 10) : (hasCustomWidths ? 12 : 16);
        const gapAdjustment =
          hasCustomWidths && columnCount > 1 ? (gapSize * (columnCount - 1)) / columnCount : 0;

        // In mobile mode, use flexbox to stack columns vertically
        if (isMobile) {
          return (
            <div
              style={{
                padding: computePadding(styles, '20px'),
                display: 'flex',
                flexDirection: 'column',
                gap: `${gapSize}px`,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                backgroundColor: styles.backgroundColor || 'transparent',
              }}
            >
              {Array.from({ length: columnCount }).map((_, index) => {
                const columnContent = renderColumnContent(content.columnContent?.[index], index);
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0px',
                      boxSizing: 'border-box',
                      width: '100%',
                      minWidth: '100%',
                      maxWidth: '100%',
                      flexShrink: 0,
                    }}
                  >
                    {columnContent}
                  </div>
                );
              })}
            </div>
          );
        }

        // Desktop: use grid for horizontal layout
        const gridTemplateColumns = Array.from({ length: columnCount })
          .map((_, idx) => {
            const width = hasCustomWidths ? columnWidths[idx] : defaultColumnWidth;
            return hasCustomWidths ? `calc(${width} - ${gapAdjustment}px)` : '1fr';
          })
          .join(' ');

        return (
          <div
            style={{
            padding: computePadding(styles, '20px'),
              display: 'grid',
              gridTemplateColumns: gridTemplateColumns,
              gap: `${gapSize}px`,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              backgroundColor: styles.backgroundColor || 'transparent',
            }}
          >
            {Array.from({ length: columnCount }).map((_, index) => {
              const columnContent = renderColumnContent(content.columnContent?.[index], index);
              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0px',
                    boxSizing: 'border-box',
                  }}
                >
                  {columnContent}
                </div>
              );
            })}
          </div>
        );

      case 'footer':
        return (
          <FooterBlock
            content={content}
            styles={styles}
            globalFontFamily={globalFontFamily}
            isMobile={isMobile}
            isPalettePreview={isPalettePreview}
          />
        );

      case 'hero-content': {
        const template = content.template || 'standard';

        if (isHeroCoreTemplate(template)) {
          return (
            <HeroContentCoreBlock
              content={content}
              styles={styles}
              globalFontFamily={globalFontFamily}
              isMobile={isMobile}
              isPalettePreview={isPalettePreview}
            />
          );
        }

        if (template === 'image-overlay') {
          const overlayMobile = editorialMobile.overlay;
          const baseHeight = parseInt(block.styles.height) || 600;
          const height = isMobile
            ? `${Math.max(overlayMobile.minHeight, Math.min(baseHeight * overlayMobile.heightMultiplier, overlayMobile.maxHeight))}px`
            : `${baseHeight}px`;
          const baseFontSize = parseInt(block.styles.fontSize) || 72;
          const fontSize = isMobile
            ? `${Math.max(overlayMobile.minFontSize, Math.min(baseFontSize * overlayMobile.fontScale, overlayMobile.maxFontSize))}px`
            : `${baseFontSize}px`;
          const textColor = block.styles.color || '#FFFFFF';
          const verticalAlign = block.styles.justifyContent || 'flex-end';
          const horizontalAlign = block.styles.textAlign || 'left';
          const lineHeight = block.styles.lineHeight || '0.85';
          const textShadow = block.styles.textShadow || 'none';
          const buttonConfig = resolveHeroOverlayButtonConfig(
            content,
            styles,
            styles.fontFamily || globalFontFamily || "'Inter', 'Helvetica Neue', Arial, sans-serif",
            isMobile
          );
          const buttonAlign =
            horizontalAlign === 'center' ? 'center' : horizontalAlign === 'right' ? 'right' : 'left';

          return (
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: height,
                backgroundImage: `url("${content.image || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80'}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                padding: computePadding(styles, isMobile ? editorialMobile.overlay.innerPadding : '60px 40px'),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: verticalAlign,
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              {/* Background overlay removed per user request */}

              <div style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                justifyContent: horizontalAlign === 'center' ? 'center' : horizontalAlign === 'right' ? 'flex-end' : 'flex-start',
                width: '100%',
                textAlign: horizontalAlign,
              }}>
                <div style={{ 
                  maxWidth: horizontalAlign === 'center' ? '100%' : '90%', 
                  width: horizontalAlign === 'center' ? '100%' : 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: horizontalAlign === 'center' ? 'center' : horizontalAlign === 'right' ? 'flex-end' : 'flex-start'
                }}>
                  <h1 style={{
                    color: textColor,
                    fontSize: fontSize,
                    fontWeight: styles.fontWeight || '900',
                    margin: 0,
                    lineHeight: lineHeight,
                    fontFamily: styles.fontFamily || globalFontFamily || "'Inter', 'Helvetica Neue', Arial, sans-serif",
                    textShadow: textShadow,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                    {content.heading || 'Add your heading here...'}
                  </h1>
                  {content.text && (
                    <p style={{
                      color: textColor,
                      fontSize: isMobile ? editorialMobile.overlay.supportingFontSize : '18px',
                      margin: '16px 0 0 0',
                      lineHeight: '1.5',
                      whiteSpace: 'pre-wrap',
                      textShadow: textShadow,
                      fontFamily: styles.fontFamily || globalFontFamily || "'Inter', 'Helvetica Neue', Arial, sans-serif",
                    }}>
                      {content.text}
                    </p>
                  )}
                  {content.buttonText && content.showButton !== false && (
                    <div
                      style={{
                        marginTop: isMobile ? '24px' : '32px',
                        textAlign: buttonAlign,
                        width: '100%',
                      }}
                      className="editorial-hero-overlay-btn-wrap"
                    >
                      <a
                        href={content.buttonUrl || '#'}
                        onClick={(e) => e.preventDefault()}
                        className="editorial-hero-overlay-button"
                        style={getHeroOverlayButtonInlineStyles(buttonConfig)}
                      >
                        {content.buttonText}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        if (template === 'hero-card') {
          const cardMobile = editorialMobile.heroCard;
          const backgroundColor = styles.backgroundColor || '#F3F4F6';
          const cardBackgroundColor = content.cardBackgroundColor || '#FFFFFF';
          const textColor = content.textColor || '#111827';
          const subtitleColor = content.subtitleColor || '#6B7280';
          const buttonColor = content.buttonColor || '#000000';
          const buttonTextColor = content.buttonTextColor || '#FFFFFF';
          const bodyTextColor = content.bodyTextColor || '#4B5563';
          const headingFontSize = isMobile ? cardMobile.headingFontSize : '48px';
          
          const cardOffset = content.cardOffset || (isMobile ? cardMobile.cardOffset : '-80px');
          
          return (
            <div style={{
              backgroundColor: backgroundColor,
              padding: isMobile ? `0 0 ${cardMobile.bottomPadding} 0` : '0 0 60px 0',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <div style={{ 
                position: 'relative', 
                width: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}>
                {/* Top Image Container */}
                <div style={{ 
                  width: '100%', 
                  maxWidth: content.imageWidth || (isMobile ? cardMobile.imageMaxWidth : '480px'),
                  zIndex: 1,
                  position: 'relative'
                }}>
                  <img 
                    src={content.image || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=1200&q=80'} 
                    alt="Hero"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>
                
                {/* Content Card Overlay */}
                <div style={{
                  width: isMobile ? cardMobile.cardWidth : 'calc(100% - 40px)',
                  maxWidth: isMobile ? cardMobile.cardMaxWidth : '520px',
                  backgroundColor: cardBackgroundColor,
                  padding: isMobile ? cardMobile.cardPadding : '48px 20px',
                  marginTop: cardOffset,
                  position: 'relative',
                  zIndex: 10,
                  textAlign: 'center',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                  boxSizing: 'border-box',
                  border: '1px solid rgba(0,0,0,0.02)',
                }}>
                {content.subtitle && (
                  <p style={{
                    margin: '0 0 12px 0',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: subtitleColor,
                    textTransform: 'uppercase',
                    letterSpacing: '0.2em',
                    fontFamily: styles.fontFamily || globalFontFamily || "'Inter', sans-serif",
                  }}>
                    {content.subtitle}
                  </p>
                )}
                <h1 style={{
                  margin: '0 0 20px 0',
                  fontSize: headingFontSize,
                  fontWeight: '800',
                  color: textColor,
                  lineHeight: '1.1',
                  fontFamily: styles.fontFamily || globalFontFamily || "'Inter', sans-serif",
                }}>
                  {content.heading || "We're Bringing Basic Back"}
                </h1>
                {content.text && (
                  <p style={{
                    margin: '0 0 32px 0',
                    fontSize: '15px',
                    color: bodyTextColor,
                    lineHeight: '1.6',
                    fontFamily: styles.fontFamily || globalFontFamily || "'Inter', sans-serif",
                  }}>
                    {content.text}
                  </p>
                )}
                {content.buttonText && content.showButton !== false && (
                  <div style={{ marginTop: '32px' }}>
                    <a
                      href={content.buttonUrl || '#'}
                      onClick={(e) => e.preventDefault()}
                      style={{
                        display: 'inline-block',
                        backgroundColor: buttonColor,
                        color: buttonTextColor,
                        padding: content.buttonPadding || '16px 48px',
                        fontSize: '12px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        textDecoration: 'none',
                        fontFamily: styles.fontFamily || globalFontFamily || "'Inter', sans-serif",
                      }}
                    >
                      {content.buttonText}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        }

        if (template === 'editorial' || template === 'quote') {
          const heroEdMobile = editorialMobile.heroEditorial;
          const quoteMobile = editorialMobile.quote;
          return (
            <div
              style={{
                padding: computePadding(
                  styles,
                  template === 'quote'
                    ? (isMobile ? quoteMobile.padding : '80px 20px')
                    : (isMobile ? heroEdMobile.padding : '80px 40px')
                ),
                backgroundColor: styles.backgroundColor || '#FFFFFF',
                fontFamily: styles.fontFamily || globalFontFamily || "'Inter', 'system-ui', sans-serif",
                textAlign: styles.textAlign || 'center',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {template === 'editorial' && (
                <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                  {content.subtitle && (
                    <p style={{
                      margin: '0 0 16px 0',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3em',
                      fontFamily: styles.fontFamily || globalFontFamily || 'system-ui, -apple-system, sans-serif',
                    }}>
                      {content.subtitle}
                    </p>
                  )}
                  <h1 style={{
                    margin: '0 0 24px 0',
                    fontSize: isMobile ? heroEdMobile.headingFontSize : '56px',
                    fontWeight: '400',
                    color: '#111827',
                    lineHeight: '1.1',
                    fontFamily: styles.fontFamily || globalFontFamily || "'Playfair Display', 'Georgia', serif",
                  }}>
                    {content.heading || 'Modern Editorial'}
                  </h1>
                  {content.image && (
                    <div style={{ margin: isMobile ? heroEdMobile.imageMargin : '32px 0', textAlign: 'center' }}>
                      <img
                        src={content.image}
                        alt="Hero"
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          borderRadius: '2px',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <p style={{
                    margin: '0 0 32px 0',
                    fontSize: isMobile ? heroEdMobile.textFontSize : '18px',
                    color: '#4B5563',
                    lineHeight: '1.7',
                    fontFamily: styles.fontFamily || globalFontFamily || 'system-ui, -apple-system, sans-serif',
                  }}>
                    {content.text || 'Bring your story to life with a beautiful, minimal layout that puts your content front and center.'}
                  </p>
                  {content.buttonText && content.showButton !== false && (
                    <a
                      href={content.buttonUrl || '#'}
                      onClick={(e) => e.preventDefault()}
                      style={{
                        display: 'inline-block',
                        backgroundColor: '#111827',
                        color: '#FFFFFF',
                        padding: content.buttonPadding || '16px 40px',
                        borderRadius: '0',
                        fontSize: '13px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {content.buttonText}
                    </a>
                  )}
                </div>
              )}
              {template === 'quote' && (
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: isMobile ? '0' : '0 20px' }}>
                  <div style={{ 
                    width: '40px', 
                    height: '2px', 
                    backgroundColor: '#E5E7EB', 
                    margin: isMobile ? quoteMobile.dividerMargin : '0 auto 32px'
                  }} />
                  <blockquote style={{
                    margin: '0 0 32px 0',
                    fontSize: isMobile ? quoteMobile.quoteFontSize : '42px',
                    fontWeight: '400',
                    color: '#111827',
                    lineHeight: '1.3',
                    fontFamily: styles.fontFamily || globalFontFamily || "'Playfair Display', 'Georgia', serif",
                    fontStyle: 'italic'
                  }}>
                    "{content.text || 'Design is not just what it looks like and feels like. Design is how it works.'}"
                  </blockquote>
                  {content.subtitle && (
                    <cite style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2em',
                      fontFamily: styles.fontFamily || globalFontFamily || 'system-ui, -apple-system, sans-serif',
                      fontStyle: 'normal'
                    }}>
                      — {content.subtitle}
                    </cite>
                  )}
                </div>
              )}
            </div>
          );
        }

        return null;
      }

      case 'image-content': {
        const template = content.template || 'standard';
        
        if (template === 'standard') {
          const imagePadding = isMobile ? '20px' : '32px';
          const contentPadding = isMobile ? '20px' : '32px';
          const headingFontSize = isMobile ? '22px' : '26px';
          const textFontSize = isMobile ? '13px' : '14px';
          const buttonFontSize = isMobile ? '13px' : '14px';

          return (
            <div
              style={{
                padding: computePadding(styles, '0px'),
                backgroundColor: styles.backgroundColor || '#FFFFFF',
                fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '0',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ padding: imagePadding, backgroundColor: '#F3F4F6', textAlign: 'center' }}>
                {content.image ? (
                  <img
                    src={content.image}
                    alt="Content"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div style={{ width: '100%', height: '200px', backgroundColor: '#E5E7EB', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg style={{ width: '48px', height: '48px', color: '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div style={{ padding: contentPadding, backgroundColor: '#FFFFFF' }}>
                {content.subtitle && (
                  <p style={{
                    margin: '0 0 8px 0',
                    fontSize: isMobile ? '12px' : '13px',
                    fontWeight: '500',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                  }}>
                    {content.subtitle}
                  </p>
                )}
                <h2 style={{
                  margin: '0 0 12px 0',
                  fontSize: headingFontSize,
                  fontWeight: 700,
                  color: '#111827',
                  fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                }}>
                  {content.heading || 'A more detailed story'}
                </h2>
                <div
                  style={{
                    fontSize: textFontSize,
                    color: '#4B5563',
                    lineHeight: '160%',
                    fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                    marginBottom: '20px',
                  }}
                >
                  {content.text ||
                    'Expand on your header with more context and information about this particular topic.'}
                </div>
                {content.buttonText && (
                  <a
                    href={content.buttonUrl || '#'}
                    onClick={(e) => e.preventDefault()}
                    style={{
                      display: 'inline-block',
                      backgroundColor: styles.buttonColor || '#000000',
                      color: styles.buttonTextColor || '#FFFFFF',
                      padding: '10px 24px',
                      borderRadius: '6px',
                      fontSize: buttonFontSize,
                      fontWeight: 600,
                      textDecoration: 'none',
                    }}
                  >
                    {content.buttonText}
                  </a>
                )}
              </div>
            </div>
          );
        }

        if (content.template === 'editorial') {
          const splitMobile = editorialMobile.split;
          const imagePadding = '0';
          const contentPadding = isMobile ? splitMobile.contentPadding : '60px 40px';
          const headingFontSize = isMobile ? splitMobile.headingFontSize : '48px';
          const textFontSize = isMobile ? splitMobile.textFontSize : '17px';

          return (
            <div
              style={{
                padding: computePadding(styles, '0px'),
                backgroundColor: styles.backgroundColor || '#FFFFFF',
                fontFamily: styles.fontFamily || globalFontFamily || "'Inter', 'system-ui', sans-serif",
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '0',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ padding: imagePadding, backgroundColor: '#F9FAFB', textAlign: 'center' }}>
                {content.image ? (
                  <img
                    src={content.image}
                    alt="Content"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div style={{ width: '100%', height: '400px', backgroundColor: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  </div>
                )}
              </div>
              <div style={{ padding: contentPadding, backgroundColor: '#FFFFFF', textAlign: 'left' }}>
                {content.subtitle && (
                  <p style={{
                    margin: '0 0 12px 0',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: isMobile ? splitMobile.subtitleLetterSpacing : '0.2em',
                    fontFamily: styles.fontFamily || globalFontFamily || 'system-ui, -apple-system, sans-serif',
                  }}>
                    {content.subtitle}
                  </p>
                )}
                <h2 style={{
                  margin: '0 0 20px 0',
                  fontSize: headingFontSize,
                  fontWeight: '400',
                  color: '#111827',
                  lineHeight: '1.1',
                  fontFamily: "'Playfair Display', 'Georgia', serif",
                }}>
                  {content.heading || 'Modern Editorial'}
                </h2>
                <p style={{
                  margin: isMobile ? '0 0 24px 0' : '0 0 32px 0',
                  fontSize: textFontSize,
                  color: '#4B5563',
                  lineHeight: '1.7',
                  fontFamily: styles.fontFamily || globalFontFamily || 'system-ui, -apple-system, sans-serif',
                }}>
                  {content.text || 'Bring your story to life with a beautiful, minimal layout that puts your content front and center.'}
                </p>
                {content.buttonText && (
                  <a
                    href={content.buttonUrl || '#'}
                    onClick={(e) => e.preventDefault()}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#111827',
                      color: '#FFFFFF',
                      padding: '14px 32px',
                      borderRadius: '0',
                      fontSize: '13px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {content.buttonText}
                  </a>
                )}
              </div>
            </div>
          );
        }

        return null;
      }

      case 'feature-showcase': {
        const template = content.template || 'standard';
        if (template === 'standard') {
          const containerPadding = isMobile ? '40px 20px' : '80px 20px';
          const imagePadding = isMobile ? '32px' : '40px';
          const contentPadding = isMobile ? '32px 0 0 0' : '40px';
          const headingFontSize = isMobile ? '24px' : '32px';
          const textFontSize = isMobile ? '15px' : '16px';
          const buttonFontSize = '14px';

          return (
            <div
              style={{
                padding: styles.padding || containerPadding,
                backgroundColor: styles.backgroundColor || '#FFFFFF',
                fontFamily: styles.fontFamily || globalFontFamily || "'Inter', 'system-ui', sans-serif",
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? '0' : '40px',
                width: '100%',
                boxSizing: 'border-box',
                alignItems: 'center',
              }}
            >
              <div style={{ padding: '0', textAlign: 'center' }}>
                {content.image ? (
                  <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '24px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.05)' }}>
                    <img
                      src={content.image}
                      alt="Feature"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ width: '100%', height: '300px', backgroundColor: '#F3F4F6', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg style={{ width: '64px', height: '64px', color: '#D1D5DB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div style={{ padding: '0', textAlign: isMobile ? 'center' : 'left' }}>
                <h2 style={{
                  margin: '0 0 20px 0',
                  fontSize: headingFontSize,
                  fontWeight: '800',
                  color: '#111827',
                  fontFamily: styles.fontFamily || globalFontFamily || "'Playfair Display', serif",
                  lineHeight: '1.2',
                }}>
                  {content.heading || 'Amazing Feature'}
                </h2>
                <p style={{
                  margin: '0 0 32px 0',
                  fontSize: textFontSize,
                  color: '#4B5563',
                  lineHeight: '1.7',
                  fontFamily: styles.fontFamily || globalFontFamily || "'Inter', sans-serif",
                }}>
                  {content.text || ''}
                </p>
                {content.buttonText && (
                  <a
                    href={content.buttonUrl || '#'}
                    onClick={(e) => e.preventDefault()}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#111827',
                      color: '#FFFFFF',
                      padding: '14px 32px',
                      borderRadius: '12px',
                      fontSize: buttonFontSize,
                      fontWeight: '700',
                      textDecoration: 'none',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    {content.buttonText}
                  </a>
                )}
              </div>
            </div>
          );
        }
        if (template === 'quote') {
          return (
            <div style={{ maxWidth: '800px', margin: '0 auto', padding: styles.padding || '80px 20px', textAlign: 'center' }}>
              <div style={{ width: '40px', height: '2px', backgroundColor: '#E5E7EB', margin: '0 auto 32px' }} />
              <blockquote style={{
                margin: '0 0 32px 0',
                fontSize: isMobile ? '28px' : '42px',
                fontWeight: '400',
                color: '#111827',
                lineHeight: '1.3',
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontStyle: 'italic'
              }}>
                "{content.text || 'Design is not just what it looks like and feels like. Design is how it works.'}"
              </blockquote>
              {content.subtitle && (
                <cite style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.2em',
                  fontFamily: styles.fontFamily || globalFontFamily || 'system-ui, -apple-system, sans-serif',
                  fontStyle: 'normal'
                }}>
                  — {content.subtitle}
                </cite>
              )}
            </div>
          );
        }
        return null;
      }

      // testimonial — handled by the full case below

      case 'cta-banner': {
        const heading = content.heading || 'Ready to Start?';
        const text = content.text || '';
        const buttonText = content.buttonText || 'Get Started';
        const buttonUrl = content.buttonUrl || '#';
        const bgColor = styles.backgroundColor || 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)';
        const textColor = styles.color || '#FFFFFF';

        return (
          <div
            style={{
              padding: styles.padding || (isMobile ? '60px 20px' : '100px 20px'),
              backgroundColor: styles.backgroundColor ? undefined : '#6366F1',
              backgroundImage: styles.backgroundColor ? undefined : bgColor,
              fontFamily: styles.fontFamily || globalFontFamily || "'Inter', 'system-ui', sans-serif",
              textAlign: 'center',
              width: '100%',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',
              color: textColor,
            }}
          >
            {/* Background decorative elements */}
            <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <div style={{ position: 'absolute', bottom: '-50px', left: '-50px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(0,0,0,0.03)' }} />

            <div style={{ position: 'relative', zIndex: 1, maxWidth: '700px', margin: '0 auto' }}>
              <h2
                style={{
                  margin: '0 0 20px 0',
                  fontSize: isMobile ? '32px' : '48px',
                  fontWeight: 850,
                  color: textColor,
                  fontFamily: styles.fontFamily || globalFontFamily || "'Playfair Display', serif",
                  lineHeight: '1.1',
                  letterSpacing: '-0.03em',
                }}
              >
                {heading}
              </h2>
              {text && (
                <p
                  style={{
                    margin: '0 0 40px 0',
                    fontSize: isMobile ? '16px' : '20px',
                    lineHeight: '1.6',
                    color: textColor,
                    opacity: 0.9,
                    fontWeight: 400,
                  }}
                >
                  {text}
                </p>
              )}
              {buttonText && (
                <a
                  href={buttonUrl}
                  onClick={(e) => e.preventDefault()}
                  style={{
                    display: 'inline-block',
                    padding: '18px 48px',
                    backgroundColor: '#FFFFFF',
                    color: '#6366F1',
                    fontWeight: 800,
                    fontSize: '15px',
                    textDecoration: 'none',
                    borderRadius: '16px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {buttonText}
                </a>
              )}
            </div>
          </div>
        );
      }

      case 'profile-card': {
        const template = content.template || 'standard';
        if (template === 'standard') {
          const profilePadding = isMobile ? '24px 20px' : '32px';
          const contentPadding = isMobile ? '24px 20px' : '32px';
          const nameFontSize = isMobile ? '14px' : '16px';
          const roleFontSize = isMobile ? '12px' : '13px';
          const eventTitleFontSize = isMobile ? '20px' : '24px';
          const eventTextFontSize = isMobile ? '13px' : '14px';
          const profileImageSize = isMobile ? '80px' : '96px';

          return (
            <div
              style={{
                padding: computePadding(styles, '0px'),
                backgroundColor: styles.backgroundColor || '#FFFFFF',
                fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '40% 60%',
                gap: '0',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ padding: profilePadding, backgroundColor: '#E0F7FA', textAlign: 'center' }}>
                {content.profileImage ? (
                  <img
                    src={content.profileImage}
                    alt="Profile"
                    style={{ width: profileImageSize, height: profileImageSize, borderRadius: '50%', objectFit: 'cover', marginBottom: '16px' }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div style={{ width: profileImageSize, height: profileImageSize, borderRadius: '50%', backgroundColor: '#B2E5E8', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg style={{ width: '48px', height: '48px', color: '#0891B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
                <h3 style={{
                  margin: '0 0 4px 0',
                  fontSize: nameFontSize,
                  fontWeight: '700',
                  color: '#111827',
                  textTransform: 'uppercase',
                  fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                }}>
                  {content.name || 'KENZI BARNES'}
                </h3>
                <p style={{
                  margin: '0',
                  fontSize: roleFontSize,
                  color: '#4B5563',
                  fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                }}>
                  {content.role || 'Campaign manager'}
                </p>
              </div>
              <div style={{ padding: contentPadding, backgroundColor: '#FFFFFF' }}>
                {content.eventLabel && (
                  <p style={{
                    margin: '0 0 4px 0',
                    fontSize: isMobile ? '11px' : '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                  }}>
                    {content.eventLabel}
                  </p>
                )}
                <h2 style={{
                  margin: '0 0 8px 0',
                  fontSize: eventTitleFontSize,
                  fontWeight: '700',
                  color: '#111827',
                  fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                }}>
                  {content.eventTitle || 'Guest Lecture'}
                </h2>
                <p style={{
                  margin: '0 0 20px 0',
                  fontSize: eventTextFontSize,
                  color: '#6B7280',
                  lineHeight: '160%',
                  fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
                }}>
                  {content.eventDescription || ''}
                </p>
                {content.buttonText && (
                  <a
                    href={content.buttonUrl || '#'}
                    onClick={(e) => e.preventDefault()}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#10B981',
                      color: '#FFFFFF',
                      padding: '12px 24px',
                      borderRadius: '9999px',
                      fontSize: '14px',
                      fontWeight: '600',
                      textDecoration: 'none',
                    }}
                  >
                    {content.buttonText}
                  </a>
                )}
              </div>
            </div>
          );
        }
        return null;
      }

      case 'video':
        return <VideoBlock content={content} styles={styles} globalFontFamily={globalFontFamily} />;

      case 'social': {
        const platforms = content.platforms || [];
        const defaults = {
          display: 'icon-only',
          iconStyle: 'solid',
          layout: 'horizontal',
          iconColor: '#000000',
          alignment: 'center',
          gap: 12,
          size: 24,
        };
        const iconSettings = { ...defaults, ...(content.iconSettings || {}) };

        const containerStyle: React.CSSProperties = {
          padding: styles.padding || '20px',
          backgroundColor: styles.backgroundColor || 'transparent',
          textAlign: (iconSettings.alignment || 'center') as any,
        };

        const linksContainerStyle: React.CSSProperties = {
          display: 'inline-flex',
          gap: `${iconSettings.gap}px`,
          justifyContent: 
            iconSettings.alignment === 'left' ? 'flex-start' : 
            iconSettings.alignment === 'right' ? 'flex-end' : 'center',
          flexWrap: 'wrap',
          flexDirection: iconSettings.layout === 'vertical' ? 'column' : 'row',
          alignItems: 'center',
        };

        const renderIcon = (platform: any) => {
          const platformData = getPlatformData(platform.icon);
          const brandColor = platformData?.color || '#64748B';
          const effectiveStyle = platform.style || iconSettings.iconStyle || 'solid';
          const isOutline = effectiveStyle.includes('outline');
          const isBrand = effectiveStyle.includes('brand');
          const isCircle = effectiveStyle.includes('circle');
          const isSquare = effectiveStyle.includes('square');
          const isShape = isCircle || isSquare;

          const iconColor = isBrand ? brandColor : (iconSettings.iconColor || '#000000');
          const size = iconSettings.size || 24;
          const containerSize = isShape ? Math.round(size * 1.6) : size;

          const baseIconStyle: React.CSSProperties = {
            width: size,
            height: size,
            display: 'block',
          };

          const shapeStyle: React.CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: containerSize,
            height: containerSize,
            backgroundColor: isOutline ? 'transparent' : iconColor,
            borderRadius: isCircle ? '50%' : '4px',
            border: isOutline ? `2px solid ${iconColor}` : 'none',
          };

          const path = platformData?.path || 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z';
          const viewBox = platformData?.viewBox || '0 0 24 24';

          if (isShape) {
            const innerIconColor = isOutline ? iconColor : (platform.icon === 'snapchat' ? '#000000' : '#FFFFFF');
            return (
              <div style={shapeStyle}>
                <svg
                  viewBox={viewBox}
                  width={size}
                  height={size}
                  fill={isOutline ? 'none' : innerIconColor}
                  stroke={isOutline ? innerIconColor : 'none'}
                  strokeWidth={isOutline ? 2 : 0}
                  style={baseIconStyle}
                >
                  <path d={path} />
                </svg>
              </div>
            );
          }

          return (
            <svg
              viewBox={viewBox}
              width={size}
              height={size}
              fill={isOutline ? 'none' : iconColor}
              stroke={isOutline ? iconColor : 'none'}
              strokeWidth={isOutline ? 2 : 0}
              style={baseIconStyle}
            >
              <path d={path} />
            </svg>
          );
        };

        return (
          <div style={containerStyle}>
            <div style={linksContainerStyle}>
              {platforms.map((platform: any, index: number) => (
                <a
                  key={index}
                  href={platform.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    color: iconSettings.iconColor,
                  }}
                >
                  {renderIcon(platform)}
                  {iconSettings.display === 'icon-text' && (
                    <span style={{ marginLeft: '8px', fontSize: '14px', fontWeight: '500' }}>
                      {platform.name}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>
        );
      }

      case 'logo':
        const logoAlign = styles.textAlign || 'center';
        // Use individual padding sides if available, otherwise use general padding
        const logoPadding = styles.paddingTop || styles.paddingBottom || styles.paddingLeft || styles.paddingRight
          ? `${styles.paddingTop || '0px'} ${styles.paddingRight || '0px'} ${styles.paddingBottom || '0px'} ${styles.paddingLeft || '0px'}`
          : (styles.padding || '20px');
        const logoLinkHref = buildActionHref(content.linkType, content.linkUrl);
        const hasLogoLink = Boolean(logoLinkHref);
        // Use responsive sizing unless user has explicitly set a custom width (not the old default '200px')
        const logoWidth = (content.width && content.width !== '200px') ? content.width : (isMobile ? '120px' : '150px');

        const logoElement = content.src ? (
          <img
            src={content.src}
            alt={content.alt || 'Logo'}
            style={{
              width: logoWidth,
              height: 'auto',
              maxWidth: '100%',
              display: 'inline-block',
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <div
            style={{
              width: logoWidth,
              height: '60px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '0 16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              background: 'linear-gradient(to bottom, #FFFFFF, #F8FAFC)',
            }}
          >
            <div style={{
              width: '24px',
              height: '24px',
              backgroundColor: '#F1F5F9',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg className="w-3.5 h-3.5 text-ocean-600/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#475569',
              textTransform: 'uppercase',
              letterSpacing: '0.025em'
            }}>
              Brand Logo
            </span>
          </div>
        );

        return (
          <div style={{
            padding: logoPadding,
            textAlign: logoAlign,
            backgroundColor: styles.backgroundColor || 'transparent',
          }}>
            {hasLogoLink ? (
              <a
                href={logoLinkHref}
                target={content.linkTarget || '_blank'}
                rel={content.linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                onClick={(event) => event.preventDefault()}
                style={{ display: 'inline-block' }}
              >
                {logoElement}
              </a>
            ) : (
              logoElement
            )}
          </div>
        );

      case 'creative-assistant':
        return (
          <div
            style={{
              padding: styles.padding || '20px',
              backgroundColor: styles.backgroundColor || '#F0F9FF',
              border: styles.border || '2px dashed #3B82F6',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>✨</div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>Creative Assistant</div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>
              {content.prompt || 'Generate creative content'}
            </div>
          </div>
        );

      case 'survey':
        return (
          <div
            style={{
              padding: styles.padding || '20px',
              backgroundColor: styles.backgroundColor || '#F8FAFC',
              borderRadius: '8px',
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '12px' }}>
              {content.title || 'Survey Question'}
            </div>
            {content.questions?.map((question: any, index: number) => (
              <div key={index} style={{ marginBottom: '12px' }}>
                <div style={{ marginBottom: '8px' }}>{question.text || 'Question'}</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <div
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    Option 1
                  </div>
                  <div
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    Option 2
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'code':
        return (
          <div
            style={{
              padding: styles.padding || '20px',
              backgroundColor: styles.backgroundColor || '#1F2937',
              color: styles.color || '#F9FAFB',
              fontFamily: styles.fontFamily || 'monospace',
              fontSize: '14px',
              borderRadius: '4px',
              overflow: 'auto',
            }}
          >
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {content.code || '<div>Custom HTML code</div>'}
            </pre>
          </div>
        );

      case 'apps':
        return (
          <div style={{
            padding: styles.padding || '20px',
            textAlign: 'center',
            backgroundColor: styles.backgroundColor || 'transparent',
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📱</div>
            <div style={{ fontWeight: '600' }}>Apps</div>
            <div style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px' }}>
              Add app integrations
            </div>
          </div>
        );

      case 'product': {
        const isEditorial = content.template === 'editorial';
        if (isEditorial) {
          const productMobile = editorialMobile.product;
          const contentPadding = isMobile ? productMobile.contentPadding : '80px 40px';
          const nameFontSize = isMobile ? productMobile.nameFontSize : '42px';
          const priceFontSize = isMobile ? productMobile.priceFontSize : '24px';

          return (
            <div
              style={{
                padding: computePadding(styles, '0px'),
                backgroundColor: styles.backgroundColor || '#FFFFFF',
                textAlign: 'center',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <img
                src={content.image || '/api/placeholder/800/800'}
                alt={content.name}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
              <div style={{ maxWidth: '600px', margin: '0 auto', padding: contentPadding, boxSizing: 'border-box' }}>
                {content.subtitle && (
                  <p style={{
                    margin: '0 0 12px 0',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.25em',
                    fontFamily: styles.productNameFontFamily || styles.fontFamily || 'system-ui, -apple-system, sans-serif',
                  }}>
                    {content.subtitle}
                  </p>
                )}
                <h2 style={{
                  margin: '0 0 16px 0',
                  fontSize: nameFontSize,
                  fontWeight: '400',
                  color: '#111827',
                  lineHeight: '1.2',
                  fontFamily: "'Playfair Display', 'Georgia', serif",
                }}>
                  {content.name || 'Editorial Collection'}
                </h2>
                <div style={{
                  fontSize: priceFontSize,
                  fontWeight: '400',
                  color: '#6B7280',
                  marginBottom: '24px',
                  fontFamily: 'serif',
                }}>
                  {content.price || '$149.00'}
                </div>
                <p style={{
                  margin: '0 0 40px 0',
                  fontSize: isMobile ? productMobile.descFontSize : '16px',
                  color: '#4B5563',
                  lineHeight: '1.7',
                  fontFamily: styles.fontFamily || globalFontFamily || 'system-ui, -apple-system, sans-serif',
                }}>
                  {content.description || 'Our signature piece designed for those who appreciate the finer details of modern craft.'}
                </p>
                <a
                  href={content.url || '#'}
                  onClick={(e) => e.preventDefault()}
                  style={{
                    display: 'inline-block',
                    backgroundColor: content.buttonColor || '#111827',
                    color: content.buttonTextColor || '#FFFFFF',
                    padding: content.buttonPadding || '16px 48px',
                    borderRadius: '0',
                    fontSize: '13px',
                    fontWeight: '600',
                    textDecoration: 'none',
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                  }}
                >
                  {content.buttonText || 'Discover More'}
                </a>
              </div>
            </div>
          );
        }

        const productPadding = isMobile ? '20px' : '24px';
        const productNameFontSize = isMobile ? '18px' : '20px';
        const productDescFontSize = isMobile ? '13px' : '14px';
        const productPriceFontSize = isMobile ? '20px' : '24px';
        const productButtonFontSize = isMobile ? '14px' : '15px';

        return (
          <div
            style={{
              padding: styles.padding || productPadding,
              backgroundColor: styles.backgroundColor || '#FFFFFF',
              fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                maxWidth: isMobile ? '100%' : '480px',
                margin: '0 auto',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  backgroundColor: '#F3F4F6',
                  borderRadius: '8px',
                  marginBottom: isMobile ? '16px' : '20px',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={content.image || '/api/placeholder/480/220'}
                  alt={content.name || 'Product'}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: productNameFontSize,
                  marginBottom: '6px',
                  color: '#111827',
                }}
              >
                {content.name || 'Product name'}
              </div>
              <div
                style={{
                  fontSize: productDescFontSize,
                  color: '#4B5563',
                  lineHeight: '1.6',
                  marginBottom: isMobile ? '10px' : '12px',
                }}
              >
                {content.description ||
                  'Describe your product in a way that highlights how it will benefit your readers.'}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'baseline',
                  gap: '12px',
                  marginBottom: isMobile ? '12px' : '16px',
                }}
              >
                <span
                  style={{
                    fontSize: productPriceFontSize,
                    fontWeight: 700,
                    color: '#111827',
                  }}
                >
                  {content.price || '$99'}
                </span>
                <span
                  style={{
                    fontSize: isMobile ? '12px' : '14px',
                    color: '#9CA3AF',
                    textDecoration: 'line-through',
                  }}
                >
                  {content.oldPrice || '$129'}
                </span>
              </div>
              <a
                href={content.url || '#'}
                onClick={(e) => e.preventDefault()}
                style={{
                  display: 'inline-block',
                  padding: isMobile ? '12px 24px' : '12px 32px',
                  backgroundColor: '#10B981',
                  color: '#FFFFFF',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontSize: productButtonFontSize,
                  fontWeight: 600,
                }}
              >
                {content.buttonText || 'Shop Now'}
              </a>
            </div>
          </div>
        );
      }

      case 'product-rec': {
        const template = content.template || 'standard';
        const productsPerRow = parseInt(content.productsPerRow || 3, 10);
        const products = Array.isArray(content.products) ? content.products : [];
        const isCenteringNeeded = productsPerRow === 3 && products.length === 2;

        if (template === 'standard') {
          return (
            <div
              style={{
                padding: styles.padding || '24px',
                backgroundColor: styles.backgroundColor || '#FFFFFF',
                fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              }}
            >
              <div
                key={`${block.id}-grid-${productsPerRow}-${products.length}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile 
                    ? '1fr' 
                    : isCenteringNeeded 
                      ? 'repeat(2, 1fr)' 
                      : `repeat(${productsPerRow}, minmax(0, 1fr))`,
                  gap: '24px',
                  justifyContent: 'center',
                  width: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  // Handle centering for 2 items in a 3-column setup
                  maxWidth: (isCenteringNeeded && !isMobile) ? '440px' : '100%',
                  margin: (isCenteringNeeded && !isMobile) ? '0 auto' : '0',
                }}
              >
                {products.map((product: any, index: number) => (
                  <div key={index} style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        backgroundColor: '#F3F4F6',
                        borderRadius: content.showRoundedCorners ? '12px' : '0',
                        marginBottom: '16px',
                        overflow: 'hidden',
                        aspectRatio: '1 / 1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={product.image || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80'}
                        alt={product.name || 'Product'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          backgroundColor: '#F3F4F6',
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (target.src !== 'https://via.placeholder.com/240x240?text=Product') {
                            target.src = 'https://via.placeholder.com/240x240?text=Product';
                          }
                        }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: styles.productNameFontSize || '15px',
                        fontWeight: styles.productNameFontWeight || 600,
                        color: styles.productNameColor || '#111827',
                        fontFamily: styles.productNameFontFamily || 'Arial, sans-serif',
                        marginBottom: '6px',
                        lineHeight: '1.4',
                      }}
                    >
                      {product.name || 'Product name'}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        marginBottom: '16px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: styles.productPriceFontSize || '15px',
                          fontWeight: styles.productPriceFontWeight || 700,
                          color: styles.productPriceColor || '#111827',
                          fontFamily: styles.productPriceFontFamily || 'Arial, sans-serif',
                        }}
                      >
                        {product.price || '$0.00'}
                      </div>
                      {product.oldPrice && (
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#9CA3AF',
                            textDecoration: 'line-through',
                          }}
                        >
                          {product.oldPrice}
                        </span>
                      )}
                    </div>
                    {product.buttonText && (
                      <a
                        href={product.url || '#'}
                        onClick={(e) => e.preventDefault()}
                        style={{
                          display: 'inline-block',
                          padding: '10px 24px',
                          backgroundColor: '#111827',
                          color: '#FFFFFF',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {product.buttonText || 'Button'}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (template === 'modern-grid') {
          return (
            <div
              style={{
                padding: styles.padding || '48px 20px',
                backgroundColor: styles.backgroundColor || '#FFFFFF',
                fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : `repeat(${productsPerRow}, minmax(0, 1fr))`,
                  gap: '40px 24px',
                }}
              >
                {content.products?.map((product: any, index: number) => (
                  <div key={index} style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        backgroundColor: '#F9FAFB',
                        borderRadius: content.showRoundedCorners ? '12px' : '0',
                        marginBottom: '20px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                        aspectRatio: '1 / 1',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={product.image || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80'}
                        alt={product.name || 'Product'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          backgroundColor: '#F9FAFB',
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (target.src !== 'https://via.placeholder.com/400x500?text=Product') {
                            target.src = 'https://via.placeholder.com/400x500?text=Product';
                          }
                        }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: styles.productNameFontSize || '16px',
                        fontWeight: styles.productNameFontWeight || 400,
                        marginBottom: '4px',
                        color: styles.productNameColor || '#111827',
                        fontFamily: styles.productNameFontFamily || "'Playfair Display', 'Georgia', serif",
                        letterSpacing: '0.02em',
                      }}
                    >
                      {product.name || 'Product name'}
                    </div>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '16px',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {product.price || '$0.00'}
                    </div>
                    {product.buttonText && (
                      <a
                        href={product.url || '#'}
                        onClick={(e) => e.preventDefault()}
                        style={{
                          display: 'inline-block',
                          padding: '10px 0',
                          width: '100%',
                          backgroundColor: 'transparent',
                          color: '#111827',
                          textDecoration: 'none',
                          borderBottom: '1.5px solid #111827',
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                        }}
                      >
                        {product.buttonText || 'Shop Now'}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return null;
      }
      case 'music-launch': {
        const layout = content.layout || 'dark';
        const bgColor = styles.backgroundColor || (layout === 'dark' ? '#020617' : layout === 'tour' ? '#F8FAFC' : '#FFFFFF');
        // Use custom text color from styles if provided, otherwise use layout defaults
        // Make text brighter on dark backgrounds for better contrast
        const textColor = styles.color || (layout === 'dark' ? '#FFFFFF' : '#111827');
        const mutedColor = layout === 'dark' ? '#D1D5DB' : layout === 'tour' ? '#64748B' : '#6B7280';
        const showDescription = content.showDescription !== false && !!(content.description && content.description.trim());
        const showTracklist =
          content.showTracklist !== false &&
          Array.isArray(content.tracks) &&
          content.tracks.some((t: any) => t && (t.title || t.duration));
        const showPlatforms =
          content.showPlatforms !== false &&
          !!(
            (content.platforms?.spotifyUrl && content.platforms.spotifyUrl.trim()) ||
            (content.platforms?.appleMusicUrl && content.platforms.appleMusicUrl.trim()) ||
            (content.platforms?.youtubeUrl && content.platforms.youtubeUrl.trim()) ||
            (content.platforms?.soundcloudUrl && content.platforms.soundcloudUrl.trim()) ||
            (content.platforms?.bandcampUrl && content.platforms.bandcampUrl.trim())
          );
        const showSecondaryButton = content.showSecondaryButton && !!content.secondaryButtonText;

        const sectionPadding = isMobile ? '24px 20px 24px 20px' : '32px 20px 32px 20px';
        // Ensure outer container has padding on all sides if not specified
        const defaultPadding = isMobile ? '0 0 0 0' : '0 0 0 0';
        const containerPadding = styles.padding || defaultPadding;

        return (
          <div
            style={{
              padding: containerPadding,
              backgroundColor: bgColor,
              fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              color: textColor,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {/* Hero Section */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile
                  ? '1fr'
                  : layout === 'light'
                    ? '1.2fr 1.2fr'
                    : layout === 'tour'
                      ? '1fr 1fr'
                      : '1fr',
                gap: isMobile ? '20px' : layout === 'tour' ? '40px' : '24px',
                alignItems: layout === 'tour' ? 'start' : 'center',
                padding: getSectionLayoutPadding(layout, isMobile),
              }}
            >
              {content.coverImage && layout !== 'tour' && (
                <div
                  style={{
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: layout === 'dark' ? '0 20px 40px rgba(0,0,0,0.5)' : '0 10px 30px rgba(15,23,42,0.15)',
                    maxHeight: '360px',
                  }}
                >
                  <img
                    src={content.coverImage}
                    alt={content.title || 'Album cover'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {content.coverImage && layout === 'tour' && (
                <div
                  style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    maxHeight: '400px',
                  }}
                >
                  <img
                    src={content.coverImage}
                    alt={content.title || 'Album cover'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div style={{ textAlign: layout === 'light' || layout === 'tour' ? 'left' : 'center' }}>
                {content.releaseType && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      backgroundColor: layout === 'dark' ? '#1F2937' : layout === 'tour' ? '#E0E7FF' : '#EFF6FF',
                      color: layout === 'tour' ? '#4F46E5' : mutedColor,
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: '12px',
                    }}
                  >
                    <span
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '999px',
                        backgroundColor: layout === 'tour' ? '#6366F1' : '#22C55E',
                        marginRight: '8px',
                      }}
                    />
                    {content.releaseType}
                    {content.releaseDate && (
                      <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                        • {content.releaseDate}
                      </span>
                    )}
                  </div>
                )}
                {content.title && (
                  <h1
                    style={{
                      margin: '0 0 12px 0',
                      fontSize: '32px',
                      fontWeight: 800,
                      letterSpacing: '-0.02em',
                      color: textColor,
                      lineHeight: '1.2',
                    }}
                  >
                    {content.title}
                  </h1>
                )}
                {content.artist && (
                  <p
                    style={{
                      margin: '0 0 16px 0',
                      fontSize: '16px',
                      fontWeight: 500,
                      color: mutedColor,
                      lineHeight: '1.5',
                    }}
                  >
                    By {content.artist}
                  </p>
                )}
                {showDescription && (
                  <p
                    style={{
                      margin: '0 0 24px 0',
                      fontSize: '15px',
                      lineHeight: '170%',
                      color: mutedColor,
                    }}
                  >
                    {content.description}
                  </p>
                )}
                {(content.primaryButtonText || showSecondaryButton) && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '12px',
                      justifyContent: layout === 'light' || layout === 'tour' ? 'flex-start' : 'center',
                      alignItems: 'center',
                    }}
                  >
                    {content.primaryButtonText && (
                      <a
                        href={content.primaryButtonUrl || '#'}
                        onClick={(e) => e.preventDefault()}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '14px 28px',
                          borderRadius: '999px',
                          backgroundColor: layout === 'tour' ? '#6366F1' : '#22C55E',
                          color: layout === 'tour' ? '#FFFFFF' : '#0F172A',
                          fontWeight: 600,
                          fontSize: '15px',
                          textDecoration: 'none',
                          minWidth: '120px',
                          textAlign: 'center',
                          transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      >
                        {content.primaryButtonText}
                      </a>
                    )}
                    {showSecondaryButton && (
                      <a
                        href={content.secondaryButtonUrl || '#'}
                        onClick={(e) => e.preventDefault()}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '14px 24px',
                          borderRadius: '999px',
                          border: `2px solid ${layout === 'dark' ? '#6B7280' : layout === 'tour' ? '#6366F1' : '#D1D5DB'}`,
                          color: layout === 'dark' ? '#FFFFFF' : layout === 'tour' ? '#6366F1' : mutedColor,
                          fontWeight: 600,
                          fontSize: '15px',
                          backgroundColor: 'transparent',
                          textDecoration: 'none',
                          minWidth: '120px',
                          textAlign: 'center',
                          transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.8';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      >
                        {content.secondaryButtonText}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Tour Dates Section - Only for tour layout */}
            {layout === 'tour' && content.tracks && content.tracks.length > 0 && (
              <div
                style={{
                  padding: sectionPadding,
                  borderTop: '1px solid #E2E8F0',
                  backgroundColor: '#FFFFFF',
                  marginTop: '24px',
                }}
              >
                <h2
                  style={{
                    margin: '0 0 20px 0',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ fontSize: '24px' }}>📅</span>
                  Tour Dates
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {content.tracks
                    ?.filter((t: any) => t && (t.title || t.duration))
                    .slice(0, 5)
                    .map((track: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '16px',
                          backgroundColor: '#F8FAFC',
                          borderRadius: '8px',
                          border: '1px solid #E2E8F0',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                            {track.title || `Date ${index + 1}`}
                          </div>
                          {track.duration && (
                            <div style={{ fontSize: '14px', color: '#64748B' }}>{track.duration}</div>
                          )}
                          {track.featured && (
                            <div style={{ fontSize: '13px', color: '#6366F1', marginTop: '4px' }}>📍 {track.featured}</div>
                          )}
                        </div>
                        <a
                          href={content.primaryButtonUrl || '#'}
                          onClick={(e) => e.preventDefault()}
                          style={{
                            display: 'inline-block',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            backgroundColor: '#6366F1',
                            color: '#FFFFFF',
                            fontWeight: 600,
                            fontSize: '13px',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Get Tickets
                        </a>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Tracklist Section */}
            {showTracklist && layout !== 'tour' && (
              <div
                style={{
                  padding: sectionPadding,
                  borderTop: `1px solid ${layout === 'dark' ? '#1F2937' : '#E5E7EB'}`,
                  backgroundColor: layout === 'dark' ? '#020617' : '#FFFFFF',
                }}
              >
                <h2
                  style={{
                    margin: '0 0 16px 0',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: textColor,
                    lineHeight: '1.4',
                  }}
                >
                  Tracklist
                </h2>
                <table
                  width="100%"
                  cellPadding={0}
                  cellSpacing={0}
                  style={{ borderCollapse: 'collapse' }}
                >
                  <tbody>
                    {content.tracks
                      ?.filter((t: any) => t && (t.title || t.duration))
                      .map((track: any, index: number) => (
                        <tr key={index}>
                          <td
                            style={{
                              padding: '8px 0',
                              fontSize: '14px',
                              color: textColor,
                              borderBottom: `1px solid ${layout === 'dark' ? '#1F2937' : '#E5E7EB'}`,
                            }}
                          >
                            <span style={{ marginRight: '12px', opacity: 0.7 }}>
                              {index + 1}.
                            </span>
                            <span style={{ fontWeight: 500 }}>{track.title || `Track ${index + 1}`}</span>
                            {track.featured && (
                              <span style={{ marginLeft: '8px', color: mutedColor, fontSize: '12px' }}>
                                (feat. {track.featured})
                              </span>
                            )}
                          </td>
                          {track.duration && (
                            <td
                              style={{
                                padding: '8px 0',
                                fontSize: '13px',
                                color: mutedColor,
                                textAlign: 'right',
                                borderBottom: `1px solid ${layout === 'dark' ? '#1F2937' : '#E5E7EB'}`,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {track.duration}
                            </td>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Platforms Section */}
            {showPlatforms && (
              <div
                style={{
                  padding: sectionPadding,
                  borderTop: `1px solid ${layout === 'dark' ? '#1F2937' : layout === 'tour' ? '#E2E8F0' : '#E5E7EB'}`,
                  backgroundColor: layout === 'dark' ? '#030712' : layout === 'tour' ? '#F8FAFC' : '#F9FAFB',
                }}
              >
                <h2
                  style={{
                    margin: '0 0 16px 0',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: textColor,
                    lineHeight: '1.4',
                  }}
                >
                  Listen on your favorite platform
                </h2>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginTop: '8px',
                  }}
                >
                  {content.platforms?.spotifyUrl && content.platforms.spotifyUrl.trim() && (
                    <a
                      href={content.platforms.spotifyUrl}
                      onClick={(e) => e.preventDefault()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        borderRadius: '999px',
                        backgroundColor: layout === 'dark' ? '#16A34A' : '#DCFCE7',
                        color: layout === 'dark' ? '#ECFDF5' : '#166534',
                        fontSize: '13px',
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      <span style={{ marginRight: '8px' }}>🎧</span> Spotify
                    </a>
                  )}
                  {content.platforms?.appleMusicUrl && content.platforms.appleMusicUrl.trim() && (
                    <a
                      href={content.platforms.appleMusicUrl}
                      onClick={(e) => e.preventDefault()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        borderRadius: '999px',
                        backgroundColor: layout === 'dark' ? '#DB2777' : '#FCE7F3',
                        color: layout === 'dark' ? '#FDF2F8' : '#9D174D',
                        fontSize: '13px',
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      <span style={{ marginRight: '8px' }}></span> Apple Music
                    </a>
                  )}
                  {content.platforms?.youtubeUrl && content.platforms.youtubeUrl.trim() && (
                    <a
                      href={content.platforms.youtubeUrl}
                      onClick={(e) => e.preventDefault()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        borderRadius: '999px',
                        backgroundColor: layout === 'dark' ? '#DC2626' : '#FEE2E2',
                        color: layout === 'dark' ? '#FEE2E2' : '#B91C1C',
                        fontSize: '13px',
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      <span style={{ marginRight: '8px' }}>▶</span> YouTube
                    </a>
                  )}
                  {content.platforms?.soundcloudUrl && content.platforms.soundcloudUrl.trim() && (
                    <a
                      href={content.soundcloudUrl}
                      onClick={(e) => e.preventDefault()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        borderRadius: '999px',
                        backgroundColor: layout === 'dark' ? '#F97316' : '#FFEDD5',
                        color: layout === 'dark' ? '#FFEDD5' : '#9A3412',
                        fontSize: '13px',
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      <span style={{ marginRight: '8px' }}>☁️</span> SoundCloud
                    </a>
                  )}
                  {content.platforms?.bandcampUrl && content.platforms.bandcampUrl.trim() && (
                    <a
                      href={content.bandcampUrl}
                      onClick={(e) => e.preventDefault()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        borderRadius: '999px',
                        backgroundColor: layout === 'dark' ? '#0F766E' : '#CCFBF1',
                        color: layout === 'dark' ? '#ECFEFF' : '#0F766E',
                        fontSize: '13px',
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      <span style={{ marginRight: '8px' }}>♪</span> Bandcamp
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'newsletter': {
        const layout = content.layout || 'classic';
        const bgColor = styles.backgroundColor || '#FFFFFF';
        const textColor = '#111827';
        const mutedColor = '#4B5563';
        const sections =
          Array.isArray(content.sections) && content.sections.length
            ? content.sections.filter(
              (section: any) =>
                section &&
                (section.enabled !== false) &&
                (section.title || section.summary)
            )
            : [];

        return (
          <div
            style={{
              padding: computePadding(styles, '0px'),
              backgroundColor: bgColor,
              fontFamily: styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
              color: textColor,
            }}
          >
            {/* Intro / Hero */}
            <div
              style={{
                padding: '24px 20px 24px 20px',
                textAlign: 'left',
              }}
            >
              {content.showImage !== false && content.image && (
                <div
                  style={{
                    marginBottom: '20px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={content.image}
                    alt={content.title || 'Newsletter'}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {content.title && (
                <h2
                  style={{
                    margin: '0 0 8px 0',
                    fontSize: '24px',
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    color: textColor,
                  }}
                >
                  {content.title}
                </h2>
              )}
              {content.intro && (
                <p
                  style={{
                    margin: '0 0 16px 0',
                    fontSize: '14px',
                    lineHeight: '1.7',
                    color: mutedColor,
                  }}
                >
                  {content.intro}
                </p>
              )}
              {content.showButton && content.buttonText && (
                <a
                  href={content.buttonUrl || '#'}
                  onClick={(e) => e.preventDefault()}
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    borderRadius: '999px',
                    backgroundColor: '#0EA5E9',
                    color: '#FFFFFF',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {content.buttonText}
                </a>
              )}
            </div>

            {/* Sections */}
            {content.showSections !== false && sections.length > 0 && (
              <div
                style={{
                  padding: isMobile ? '8px 20px 24px 20px' : '8px 20px 32px 20px',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      isMobile ? '1fr' : (layout === 'classic' ? 'repeat(auto-fit, minmax(0, 1fr))' : '1fr'),
                    gap: isMobile ? '12px' : '16px',
                  }}
                >
                  {sections.map((section: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        padding: isMobile ? '12px 0' : '12px 12px 12px 0',
                        borderTop: '1px solid #E5E7EB',
                      }}
                    >
                      {section.title && (
                        <div
                          style={{
                            fontSize: isMobile ? '13px' : '14px',
                            fontWeight: 600,
                            marginBottom: '4px',
                            color: textColor,
                          }}
                        >
                          {section.title}
                        </div>
                      )}
                      {section.summary && (
                        <div
                          style={{
                            fontSize: isMobile ? '12px' : '13px',
                            lineHeight: '1.6',
                            color: mutedColor,
                          }}
                        >
                          {section.summary}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'event-announcement': {
        const layout = content.layout || 'standard';
        const bgColor = styles.backgroundColor || (layout === 'webinar' ? '#FFFFFF' : '#0F172A');
        const textColor = layout === 'webinar' ? '#111827' : '#F9FAFB';
        const mutedColor = layout === 'webinar' ? '#6B7280' : '#9CA3AF';
        const accentColor = layout === 'webinar' ? '#6366F1' : '#10B981';

        return (
          <div
            style={{
              padding: computePadding(styles, '0px'),
              backgroundColor: bgColor,
              fontFamily: styles.fontFamily || globalFontFamily || "'Inter', 'system-ui', sans-serif",
              color: textColor,
              overflow: 'hidden',
            }}
          >
            {content.heroImage && (
              <div style={{ padding: '0', position: 'relative' }}>
                <img
                  src={content.heroImage}
                  alt={content.title || 'Event'}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    maxHeight: layout === 'webinar' ? '450px' : '500px',
                    objectFit: 'cover',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                {layout !== 'elegant' && (
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    backgroundColor: accentColor,
                    color: '#FFFFFF',
                    padding: '8px 16px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                  }}>
                    {layout === 'webinar' ? 'Live Webinar' : 'Event'}
                  </div>
                )}
              </div>
            )}
            <div style={{
              padding: isMobile
                ? (layout === 'webinar' ? '40px 20px' : '60px 20px')
                : (layout === 'webinar' ? '60px 20px' : '80px 20px'),
              textAlign: layout === 'webinar' ? 'left' : 'center',
              maxWidth: '800px',
              margin: '0 auto',
            }}>
              {content.title && (
                <h1
                  style={{
                    margin: '0 0 20px 0',
                    fontSize: isMobile ? '32px' : '48px',
                    fontWeight: 850,
                    color: textColor,
                    fontFamily: "'Playfair Display', serif",
                    lineHeight: '1.1',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {content.title}
                </h1>
              )}
              {content.description && (
                <p
                  style={{
                    margin: '0 0 40px 0',
                    fontSize: isMobile ? '16px' : '18px',
                    lineHeight: '1.6',
                    color: mutedColor,
                  }}
                >
                  {content.description}
                </p>
              )}

              {/* Event Metadata Grid */}
              {content.showDetails && (content.eventDate || content.eventTime || content.eventLocation) && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '24px',
                  marginBottom: '40px',
                  textAlign: 'left',
                  padding: '32px',
                  backgroundColor: layout === 'webinar' ? '#F8FAFC' : 'rgba(255,255,255,0.03)',
                  borderRadius: '24px',
                  border: `1px solid ${layout === 'webinar' ? '#F1F5F9' : 'rgba(255,255,255,0.05)'}`,
                }}>
                  {content.eventDate && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: accentColor, marginBottom: '8px' }}>Date</div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: textColor }}>{content.eventDate}</div>
                    </div>
                  )}
                  {content.eventTime && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: accentColor, marginBottom: '8px' }}>Time</div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: textColor }}>{content.eventTime}</div>
                    </div>
                  )}
                  {content.eventLocation && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: accentColor, marginBottom: '8px' }}>Location</div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: textColor }}>{content.eventLocation}</div>
                    </div>
                  )}
                </div>
              )}

              {content.buttonText && (
                <a
                  href={content.buttonUrl || '#'}
                  onClick={(e) => e.preventDefault()}
                  style={{
                    display: 'inline-block',
                    padding: '18px 48px',
                    borderRadius: '16px',
                    backgroundColor: accentColor,
                    color: '#FFFFFF',
                    fontWeight: 800,
                    fontSize: '15px',
                    textDecoration: 'none',
                    boxShadow: `0 20px 25px -5px ${accentColor}33`,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {content.buttonText}
                </a>
              )}
            </div>

            {layout === 'webinar' && content.showSpeaker && content.speakerName && (
              <div style={{
                padding: isMobile ? '40px 20px' : '60px 20px',
                backgroundColor: '#F8FAFC',
                borderTop: '1px solid #F1F5F9',
                display: 'flex',
                gap: '24px',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'center' : 'flex-start',
                textAlign: isMobile ? 'center' : 'left',
              }}>
                {content.speakerImage && (
                  <img
                    src={content.speakerImage}
                    alt={content.speakerName}
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '4px solid #FFFFFF',
                      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: accentColor, marginBottom: '8px' }}>Guest Speaker</div>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827', marginBottom: '4px' }}>
                    {content.speakerName}
                  </div>
                  {content.speakerRole && (
                    <div style={{ fontSize: '14px', color: '#64748B', marginBottom: '16px', fontWeight: 500 }}>
                      {content.speakerRole}
                    </div>
                  )}
                  {content.speakerBio && (
                    <div style={{ fontSize: '15px', lineHeight: '1.6', color: '#4B5563', maxWidth: '500px' }}>
                      {content.speakerBio}
                    </div>
                  )}
                </div>
              </div>
            )}

            {layout === 'elegant' && (
              <div
                style={{
                  padding: styles.padding || (isMobile ? '80px 20px' : '120px 20px'),
                  backgroundColor: bgColor,
                  fontFamily: "'Playfair Display', serif",
                  color: textColor,
                  textAlign: 'center',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {content.heroImage && (
                  <div style={{ marginBottom: '60px', position: 'relative', display: 'inline-block', maxWidth: '800px' }}>
                    <img
                      src={content.heroImage}
                      alt={content.title || 'Event'}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        borderRadius: '0',
                        boxShadow: '0 30px 60px rgba(0,0,0,0.2)',
                      }}
                    />
                  </div>
                )}
                <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                  {content.title && (
                    <h1
                      style={{
                        margin: '0 0 32px 0',
                        fontSize: isMobile ? '40px' : '64px',
                        fontWeight: 400,
                        color: textColor,
                        lineHeight: '1.1',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {content.title}
                    </h1>
                  )}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: isMobile ? '20px' : '40px',
                    marginBottom: '48px',
                    borderTop: `1px solid ${mutedColor}44`,
                    borderBottom: `1px solid ${mutedColor}44`,
                    padding: '24px 0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3em',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: textColor,
                    flexWrap: 'wrap',
                  }}>
                    {content.eventDate && <span>{content.eventDate}</span>}
                    {content.eventTime && <span>{content.eventTime}</span>}
                    {content.eventLocation && <span>{content.eventLocation}</span>}
                  </div>
                  {content.description && (
                    <p
                      style={{
                        margin: '0 0 60px 0',
                        fontSize: isMobile ? '18px' : '20px',
                        lineHeight: '1.8',
                        color: mutedColor,
                        fontFamily: styles.fontFamily || globalFontFamily || "'Inter', sans-serif",
                        fontWeight: 300,
                      }}
                    >
                      {content.description}
                    </p>
                  )}
                  {content.buttonText && (
                    <a
                      href={content.buttonUrl || '#'}
                      onClick={(e) => e.preventDefault()}
                      style={{
                        display: 'inline-block',
                        padding: '20px 60px',
                        backgroundColor: textColor,
                        color: bgColor,
                        fontWeight: 700,
                        fontSize: '12px',
                        textDecoration: 'none',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4em',
                        borderRadius: '0',
                        transition: 'opacity 0.2s ease',
                      }}
                    >
                      {content.buttonText}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'testimonial': {
        // Single source of truth: renderTestimonialHtml() — identical output in canvas & email
        const fontFam = styles.fontFamily || globalFontFamily || 'Arial, sans-serif';
        const html = renderTestimonialHtml(content, styles, fontFam, isMobile);
        return (
          <div
            dangerouslySetInnerHTML={{ __html: html }}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
        );
      }

      case 'pricing-table': {
        const plans = Array.isArray(content.plans) ? content.plans.filter((p: any) => p && p.enabled !== false) : [];
        const mobileStyles = block.mobileStyles || {};

        return (
          <div
            style={{
              padding: computePadding(styles, '0px'),
              backgroundColor: styles.backgroundColor || '#FFFFFF',
              fontFamily: styles.fontFamily || "'Inter', 'system-ui', sans-serif",
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {content.sectionTitle && (
              <div
                style={{
                  padding: isMobile ? '40px 20px 20px 20px' : '48px 20px 32px 20px',
                  fontSize: isMobile ? '24px' : '32px',
                  fontWeight: 800,
                  color: '#111827',
                  textAlign: 'center',
                  maxWidth: isMobile ? '100%' : '900px',
                  margin: '0 auto',
                  width: '100%',
                  boxSizing: 'border-box',
                  fontFamily: "'Playfair Display', serif",
                  letterSpacing: '-0.02em',
                }}
              >
                {content.sectionTitle}
              </div>
            )}
            {plans.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(plans.length, 3)}, 1fr)`,
                  gap: isMobile ? '24px' : '20px',
                  padding: isMobile ? '0 20px 40px 20px' : '0 24px 60px 24px',
                  maxWidth: isMobile ? '100%' : '1100px',
                  margin: '0 auto',
                  width: '100%',
                  boxSizing: 'border-box',
                  alignItems: 'stretch',
                }}
              >
                {plans.map((plan: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      padding: isMobile ? '32px 24px' : '32px 20px',
                      backgroundColor: plan.highlighted ? '#FFFFFF' : '#F9FAFB',
                      borderRadius: '24px',
                      border: plan.highlighted ? '2px solid #6366F1' : '1px solid #E5E7EB',
                      textAlign: 'center',
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0,
                      position: 'relative',
                      boxShadow: plan.highlighted ? '0 20px 25px -5px rgba(99, 102, 241, 0.1), 0 10px 10px -5px rgba(99, 102, 241, 0.04)' : 'none',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {plan.highlighted && (
                      <div style={{
                        position: 'absolute',
                        top: '-12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#6366F1',
                        color: '#FFFFFF',
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '10px',
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        MOST POPULAR
                      </div>
                    )}
                    <div style={{ marginBottom: '24px' }}>
                      {plan.name && (
                        <div style={{ fontSize: '13px', fontWeight: 700, color: plan.highlighted ? '#6366F1' : '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                          {plan.name}
                        </div>
                      )}
                      {plan.price && (
                        <div style={{ fontSize: isMobile ? '28px' : '32px', fontWeight: 850, color: '#111827', display: 'flex', alignItems: 'baseline', justifyContent: 'center', letterSpacing: '-0.03em' }}>
                          {plan.price}
                          {plan.period && (
                            <span style={{ fontSize: '13px', fontWeight: 400, color: '#94A3B8', marginLeft: '4px' }}>/{plan.period}</span>
                          )}
                        </div>
                      )}
                      {plan.description && (
                        <div style={{ fontSize: '13px', color: '#64748B', marginTop: '8px', lineHeight: '1.4' }}>
                          {plan.description}
                        </div>
                      )}
                    </div>

                    <div style={{ marginBottom: '32px', flex: 1 }}>
                      {Array.isArray(plan.features) && plan.features.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left' }}>
                          {plan.features.map((feature: string, idx: number) => (
                            <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                              <div style={{ color: plan.highlighted ? '#6366F1' : '#10B981', flexShrink: 0, marginTop: '2px' }}>
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <span style={{ fontSize: '12px', color: '#475569', lineHeight: '1.4' }}>{feature}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {plan.buttonText && (
                      <a
                        href={plan.buttonUrl || '#'}
                        onClick={(e) => e.preventDefault()}
                        style={{
                          display: 'block',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          backgroundColor: plan.highlighted ? '#6366F1' : '#111827',
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: '13px',
                          textDecoration: 'none',
                          width: '100%',
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {plan.buttonText}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'features-grid':
        return (
          <FeaturesGridBlock
            content={content}
            styles={styles}
            isMobile={isMobile}
            computePadding={computePadding}
          />
        );

      case 'promo-banner': {
        const layout = content.layout || 'with-countdown';
        const bgColor = styles.backgroundColor || '#DC2626';
        const textColor = '#FFFFFF';

        return (
          <div
            style={{
              padding: computePadding(styles, '0px'),
              backgroundColor: bgColor,
              fontFamily: styles.fontFamily || 'Arial, sans-serif',
              color: textColor,
            }}
          >
            {content.heroImage && (
              <div style={{ padding: '0' }}>
                <img
                  src={content.heroImage}
                  alt={content.title || 'Promotion'}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    maxHeight: '400px',
                    objectFit: 'cover',
                  }}
                />
              </div>
            )}
            <div style={{ padding: isMobile ? '32px 20px' : '48px 32px', textAlign: 'center' }}>
              {content.title && (
                <h1
                  style={{
                    margin: '0 0 16px 0',
                    fontSize: isMobile ? '24px' : '32px',
                    fontWeight: 800,
                    color: textColor,
                  }}
                >
                  {content.title}
                </h1>
              )}
              {content.description && (
                <p
                  style={{
                    margin: '0 0 24px 0',
                    fontSize: isMobile ? '14px' : '16px',
                    lineHeight: '170%',
                    color: textColor,
                    opacity: 0.95,
                  }}
                >
                  {content.description}
                </p>
              )}
              {content.buttonText && (
                <a
                  href={content.buttonUrl || '#'}
                  onClick={(e) => e.preventDefault()}
                  style={{
                    display: 'inline-block',
                    padding: isMobile ? '12px 20px' : '12px 24px',
                    borderRadius: '999px',
                    backgroundColor: '#FFFFFF',
                    color: bgColor,
                    fontWeight: 600,
                    fontSize: '14px',
                    textDecoration: 'none',
                  }}
                >
                  {content.buttonText}
                </a>
              )}
            </div>
            {layout === 'with-countdown' && content.showCountdown && content.countdownDate && (
              <div
                style={{
                  padding: '32px',
                  backgroundColor: '#FFFFFF',
                  borderTop: `1px solid ${bgColor}`,
                }}
              >
                {content.countdownTitle && (
                  <div
                    style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#111827',
                      textAlign: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    {content.countdownTitle}
                  </div>
                )}
                <div style={{ textAlign: 'center', fontSize: '32px', fontWeight: 700, color: '#111827' }}>
                  {/* Countdown will be rendered by countdown block if needed */}
                  <div style={{ fontSize: '14px', color: '#6B7280' }}>
                    Countdown: {new Date(content.countdownDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'blog-preview': {
        return (
          <div
            style={{
              padding: computePadding(styles, '0px'),
              backgroundColor: styles.backgroundColor || '#FFFFFF',
              fontFamily: styles.fontFamily || 'Arial, sans-serif',
            }}
          >
            {content.showImage && content.image && (
              <div style={{ padding: '0' }}>
                <img
                  src={content.image}
                  alt={content.title || 'Blog post'}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    maxHeight: '400px',
                    objectFit: 'cover',
                  }}
                />
              </div>
            )}
            <div style={{ padding: isMobile ? '20px' : '24px 20px 40px 20px' }}>
              {content.showCategory && content.category && (
                <div
                  style={{
                    fontSize: isMobile ? '11px' : '12px',
                    fontWeight: 600,
                    color: '#6366F1',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '8px',
                  }}
                >
                  {content.category}
                </div>
              )}
              {content.title && (
                <h2
                  style={{
                    margin: '0 0 16px 0',
                    fontSize: isMobile ? '22px' : '28px',
                    fontWeight: 700,
                    color: '#111827',
                  }}
                >
                  {content.title}
                </h2>
              )}
              {content.excerpt && (
                <p
                  style={{
                    margin: '0 0 24px 0',
                    fontSize: isMobile ? '14px' : '15px',
                    lineHeight: '170%',
                    color: '#4B5563',
                  }}
                >
                  {content.excerpt}
                </p>
              )}
              {content.buttonText && (
                <a
                  href={content.buttonUrl || '#'}
                  onClick={(e) => e.preventDefault()}
                  style={{
                    display: 'inline-block',
                    padding: isMobile ? '10px 18px' : '10px 20px',
                    borderRadius: '6px',
                    backgroundColor: '#6366F1',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: isMobile ? '13px' : '14px',
                    textDecoration: 'none',
                  }}
                >
                  {content.buttonText}
                </a>
              )}
            </div>
          </div>
        );
      }

      case 'faq-section': {
        const faqs = Array.isArray(content.faqs) ? content.faqs.filter((f: any) => f && f.enabled !== false) : [];

        return (
          <div
            style={{
              padding: computePadding(styles, '0px'),
              backgroundColor: styles.backgroundColor || '#FFFFFF',
              fontFamily: styles.fontFamily || 'Arial, sans-serif',
            }}
          >
            {content.sectionTitle && (
              <div
                style={{
                  padding: isMobile ? '24px 20px 16px 20px' : '40px 40px 20px 40px',
                  fontSize: isMobile ? '22px' : '28px',
                  fontWeight: 700,
                  color: '#111827',
                  textAlign: 'center',
                }}
              >
                {content.sectionTitle}
              </div>
            )}
            {faqs.length > 0 && (
              <div style={{ padding: isMobile ? '0 20px 24px 20px' : '0 40px 40px 40px' }}>
                {faqs.map((faq: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: index < faqs.length - 1 ? (isMobile ? '20px' : '24px') : '0',
                      paddingBottom: index < faqs.length - 1 ? (isMobile ? '20px' : '24px') : '0',
                      borderBottom: index < faqs.length - 1 ? '1px solid #E5E7EB' : 'none',
                    }}
                  >
                    {faq.question && (
                      <div
                        style={{
                          fontSize: isMobile ? '15px' : '16px',
                          fontWeight: 600,
                          color: '#111827',
                          marginBottom: '8px',
                        }}
                      >
                        Q: {faq.question}
                      </div>
                    )}
                    {faq.answer && (
                      <div style={{ fontSize: isMobile ? '14px' : '15px', lineHeight: '180%', color: '#4B5563' }}>
                        A: {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'team-grid': {
        const members = Array.isArray(content.members)
          ? content.members.filter((m: any) => m && m.enabled !== false)
          : [];

        return (
          <div
            style={{
              padding: computePadding(styles, '0px'),
              backgroundColor: styles.backgroundColor || '#FFFFFF',
              fontFamily: styles.fontFamily || 'Arial, sans-serif',
            }}
          >
            {content.sectionTitle && (
              <div
                style={{
                  padding: isMobile ? '24px 20px 16px 20px' : '40px 40px 20px 40px',
                  fontSize: isMobile ? '22px' : '28px',
                  fontWeight: 700,
                  color: '#111827',
                  textAlign: 'center',
                }}
              >
                {content.sectionTitle}
              </div>
            )}
            {members.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(members.length, 3)}, 1fr)`,
                  gap: isMobile ? '24px' : '32px',
                  padding: isMobile ? '0 20px 24px 20px' : '0 24px 40px 24px',
                  maxWidth: isMobile ? '100%' : '900px',
                  margin: '0 auto',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {members.map((member: any, index: number) => (
                  <div key={index} style={{ textAlign: 'center' }}>
                    {member.image && (
                      <img
                        src={member.image}
                        alt={member.name}
                        style={{
                          width: isMobile ? '100px' : '120px',
                          height: isMobile ? '100px' : '120px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          margin: '0 auto 16px auto',
                          display: 'block',
                        }}
                      />
                    )}
                    {member.name && (
                      <div
                        style={{
                          fontSize: isMobile ? '15px' : '16px',
                          fontWeight: 600,
                          color: '#111827',
                          marginBottom: '4px',
                        }}
                      >
                        {member.name}
                      </div>
                    )}
                    {member.role && (
                      <div style={{ fontSize: isMobile ? '13px' : '14px', color: '#6B7280' }}>{member.role}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'countdown': {
        const CountdownTimer: React.FC = () => {
          const [timeRemaining, setTimeRemaining] = React.useState<{
            days: number;
            hours: number;
            minutes: number;
            seconds: number;
            expired: boolean;
          }>({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: false });

          React.useEffect(() => {
            const targetDate = content.targetDate || content.endDate;
            if (!targetDate) {
              setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
              return;
            }

            const calculateTimeRemaining = () => {
              const now = new Date().getTime();
              const target = new Date(targetDate).getTime();
              const difference = target - now;

              if (difference <= 0) {
                setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
                return;
              }

              const days = Math.floor(difference / (1000 * 60 * 60 * 24));
              const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
              const seconds = Math.floor((difference % (1000 * 60)) / 1000);

              setTimeRemaining({ days, hours, minutes, seconds, expired: false });
            };

            calculateTimeRemaining();
            const interval = setInterval(calculateTimeRemaining, 1000);

            return () => clearInterval(interval);
          }, [content.targetDate, content.endDate]);

          const layout = content.layout || 'horizontal';
          const showLabels = content.showLabels !== false;
          const expiredMessage = content.expiredMessage || 'The countdown has ended';
          const labelStyle = content.labelStyle || {};
          const numberStyle = content.numberStyle || {};

          const unitStyle: React.CSSProperties = {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 16px',
            backgroundColor: styles.backgroundColor || '#FFFFFF',
            borderRadius: '8px',
            border: `1px solid ${styles.borderColor || '#E5E7EB'}`,
            minWidth: '70px',
            ...numberStyle,
          };

          const numberTextStyle: React.CSSProperties = {
            fontSize: styles.fontSize || '32px',
            fontWeight: styles.fontWeight || '700',
            color: styles.color || '#111827',
            fontFamily: styles.fontFamily || 'Arial, sans-serif',
            lineHeight: '1.2',
            marginBottom: showLabels ? '4px' : '0',
          };

          const labelTextStyle: React.CSSProperties = {
            fontSize: '12px',
            fontWeight: '500',
            color: labelStyle.color || '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: styles.fontFamily || 'Arial, sans-serif',
          };

          if (timeRemaining.expired) {
            return (
              <div
                style={{
                  padding: styles.padding || '20px',
                  textAlign: styles.textAlign || 'center',
                  backgroundColor: styles.backgroundColor || 'transparent',
                }}
              >
                <div
                  style={{
                    fontSize: styles.fontSize || '18px',
                    fontWeight: styles.fontWeight || '600',
                    color: styles.color || '#6B7280',
                    fontFamily: styles.fontFamily || 'Arial, sans-serif',
                  }}
                >
                  {expiredMessage}
                </div>
              </div>
            );
          }

          const units = [
            { value: timeRemaining.days, label: 'Days' },
            { value: timeRemaining.hours, label: 'Hours' },
            { value: timeRemaining.minutes, label: 'Minutes' },
            { value: timeRemaining.seconds, label: 'Seconds' },
          ];

          const containerStyle: React.CSSProperties = {
            padding: styles.padding || '20px',
            textAlign: styles.textAlign || 'center',
            backgroundColor: styles.backgroundColor || 'transparent',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          };

          if (layout === 'vertical') {
            containerStyle.flexDirection = 'column';
            containerStyle.gap = '8px';
          }

          return (
            <div style={containerStyle}>
              {content.title && (
                <div
                  style={{
                    width: '100%',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: styles.color || '#111827',
                    marginBottom: '16px',
                    fontFamily: styles.fontFamily || 'Arial, sans-serif',
                  }}
                >
                  {content.title}
                </div>
              )}
              {units.map((unit, index) => (
                <div key={index} style={unitStyle}>
                  <div style={numberTextStyle}>{String(unit.value).padStart(2, '0')}</div>
                  {showLabels && <div style={labelTextStyle}>{unit.label}</div>}
                </div>
              ))}
            </div>
          );
        };

        return <CountdownTimer />;
      }

      default:
        return (
          <div style={{ padding: '20px', color: '#6B7280' }}>
            Unknown block type: {type}
          </div>
        );
    }
  };

  return <>{renderContent()}</>;
};

